/**
 * Espanhol do Chile.
 *
 * A chave é a frase em português, exatamente como está escrita no componente —
 * inclusive acento, travessão e reticências. Um caractere diferente e a frase
 * cai em português na tela, em silêncio; se alguma aparecer em português
 * rodando em espanhol, é aqui que a chave está diferente do código.
 *
 * Escolhas de vocabulário que valem para o arquivo inteiro, para não divergirem
 * de uma tela para a outra:
 *
 * - `inbox` → **bandeja**. É o termo que um usuário de CRM reconhece em
 *   espanhol; "inbox" solto leria como palavra em inglês no meio da frase.
 * - `atendente` → **agente**. "Atendente" não existe com esse sentido, e
 *   "ejecutivo" restringe a vendas — o papel aqui também atende suporte.
 * - `transmissão` → **difusión**, que é como o próprio Telegram chama.
 * - `relatórios` → **reportes**, e não "informes": é o uso corrente na América
 *   Latina, e Chile em particular.
 * - `workspace` fica como está, igual ao português — é o nome que o produto
 *   usa, e traduzi-lo criaria duas palavras para a mesma coisa.
 *
 * Tratamento: **tú**, nunca "usted" nem voseo. O CRM fala com quem trabalha
 * nele todo dia, e o "usted" soaria como carta de banco.
 */
export const ES_CL: Record<string, string> = {
  // ─── Datas e prévias (lib/format.ts) ───────────────────────────────────────
  Hoje: 'Hoy',
  Ontem: 'Ayer',
  'agora há pouco': 'hace un momento',
  'há {n} min': 'hace {n} min',
  'há {n}h': 'hace {n} h',
  'há {n} dia': 'hace {n} día',
  'há {n} dias': 'hace {n} días',
  'há {n} mês': 'hace {n} mes',
  'há {n} meses': 'hace {n} meses',
  'Sem nome': 'Sin nombre',
  'Sem mensagens': 'Sin mensajes',
  Mensagem: 'Mensaje',
  Foto: 'Foto',
  'Mensagem de voz': 'Mensaje de voz',
  Vídeo: 'Video',
  'Vídeo circular': 'Video circular',
  Áudio: 'Audio',
  Arquivo: 'Archivo',
  Figurinha: 'Sticker',
  Localização: 'Ubicación',
  Contato: 'Contacto',
  Enquete: 'Encuesta',
  'Nota interna': 'Nota interna',
  'Mensagem não suportada': 'Mensaje no compatible',

  // ─── Casca do app (routes/_app.tsx, __root.tsx) ────────────────────────────
  'Carregando…': 'Cargando…',
  'Redirecionando…': 'Redirigiendo…',
  'Carregando workspace…': 'Cargando workspace…',
  'Abrir menu': 'Abrir menú',
  'Reconectando…': 'Reconectando…',
  'Sem conexão — mensagens novas podem não aparecer':
    'Sin conexión — los mensajes nuevos podrían no aparecer',
  'Página não encontrada': 'Página no encontrada',
  'Voltar ao início': 'Volver al inicio',
  'Criar seu workspace': 'Crear tu workspace',
  'Vamos começar com dois inboxes — {comercial} e {suporte} — cada um com seu próprio funil e sua própria equipe. Você pode renomear, remover ou criar outros depois.':
    'Partamos con dos bandejas — {comercial} y {suporte} — cada una con su propio embudo y su propio equipo. Puedes renombrarlas, eliminarlas o crear otras después.',
  Comercial: 'Comercial',
  Suporte: 'Soporte',
  'Nome da empresa': 'Nombre de la empresa',
  'Criando…': 'Creando…',
  'Criar workspace': 'Crear workspace',

  // ─── Menu (components/NavRail.tsx) ─────────────────────────────────────────
  Inbox: 'Bandeja',
  Contatos: 'Contactos',
  Campanhas: 'Campañas',
  Transmissão: 'Difusión',
  Agendados: 'Programados',
  Relatórios: 'Reportes',
  'Respostas rápidas': 'Respuestas rápidas',
  Automações: 'Automatizaciones',
  Tags: 'Etiquetas',
  'Excluir tags': 'Excluir etiquetas',
  Nenhuma: 'Ninguna',
  'Contas do Telegram': 'Cuentas de Telegram',
  Configurações: 'Configuración',
  'Navegação principal': 'Navegación principal',
  'Usar tema claro': 'Usar tema claro',
  'Usar tema escuro': 'Usar tema oscuro',
  'Expandir menu': 'Expandir menú',
  'Recolher menu': 'Contraer menú',
  Recolher: 'Contraer',
  'Sair da conta': 'Cerrar sesión',
  'Fechar menu': 'Cerrar menú',

  // ─── Papéis ────────────────────────────────────────────────────────────────
  Dono: 'Dueño',
  Administrador: 'Administrador',
  Atendente: 'Agente',
  atendente: 'agente',

  // ─── Configurações (routes/_app/configuracoes.tsx) ─────────────────────────
  Equipe: 'Equipo',
  'Quem é {atendente} enxerga apenas os inboxes de que é membro. Dono e administrador enxergam todos.':
    'Quien es {atendente} ve solo las bandejas de las que es miembro. Dueño y administrador las ven todas.',
  'Convites por link': 'Invitaciones por enlace',
  'Quem abrir o link entra ou cria uma conta e é adicionado ao workspace. O link vale por 7 dias e pode ser revogado a qualquer momento.':
    'Quien abra el enlace entra o crea una cuenta y es agregado al workspace. El enlace dura 7 días y puede revocarse en cualquier momento.',
  Inboxes: 'Bandejas',
  'Cada inbox tem funil, fila e equipe próprios. Um {atendente} só enxerga conversas dos inboxes de que é membro; dono e administrador enxergam todos.':
    'Cada bandeja tiene su propio embudo, su propia cola y su propio equipo. Un {atendente} ve solo las conversaciones de las bandejas de las que es miembro; dueño y administrador las ven todas.',
  Logs: 'Registros',
  'O que foi feito no workspace: contas, inboxes, convites, membros e apagamentos. Do mais recente para o mais antigo.':
    'Lo que se hizo en el workspace: cuentas, bandejas, invitaciones, miembros y eliminaciones. De lo más reciente a lo más antiguo.',
  'Papel de {nome}': 'Rol de {nome}',
  você: 'tú',
  Remover: 'Eliminar',
  'Confirmar remoção': 'Confirmar eliminación',
  'Ao remover alguém, as conversas atribuídas a essa pessoa ficam sem responsável — não são apagadas. Você não consegue alterar o próprio papel nem remover a si mesmo.':
    'Al eliminar a alguien, las conversaciones asignadas a esa persona quedan sin responsable — no se borran. No puedes cambiar tu propio rol ni eliminarte a ti mismo.',
  'Para quem é? (opcional)': '¿Para quién es? (opcional)',
  'Inboxes que o atendente vai enxergar — sem nenhum marcado, ele entra sem acesso a conversas.':
    'Bandejas que el agente va a ver — sin ninguna marcada, entra sin acceso a conversaciones.',
  'Permitir vários usos (link de equipe)': 'Permitir varios usos (enlace de equipo)',
  'Gerando…': 'Generando…',
  'Gerar link': 'Generar enlace',
  'Sem rótulo': 'Sin etiqueta',
  Revogar: 'Revocar',
  Copiar: 'Copiar',
  Copiado: 'Copiado',
  // O sujeito é "la invitación", feminino em espanhol — daí a concordância.
  Aceito: 'Aceptada',
  'Aceito por {nome}': 'Aceptada por {nome}',
  'Aceito por {n} pessoas': 'Aceptada por {n} personas',
  Revogado: 'Revocada',
  Expirado: 'Expirada',
  Esgotado: 'Agotada',
  ilimitado: 'ilimitado',
  '{n} uso(s)': '{n} uso(s)',
  'Conversas que {nome} enxerga. Sem nenhum marcado, não enxerga nada.':
    'Conversaciones que {nome} ve. Sin ninguna marcada, no ve nada.',

  // ─── Escolha de idioma (components/EscolhaDeIdioma.tsx) ────────────────────
  Idioma: 'Idioma',
  'Vale só para você. Sem escolha, o CRM abre no idioma do seu aparelho.':
    'Aplica solo para ti. Sin una elección, el CRM abre en el idioma de tu dispositivo.',
  Salvar: 'Guardar',
  'Salvando…': 'Guardando…',

  // ─── Vocabulário de ação, repetido em várias telas ─────────────────────────
  Cancelar: 'Cancelar',
  'Cancelando…': 'Cancelando…',
  Aplicar: 'Aplicar',
  'Aplicando…': 'Aplicando…',
  Apagar: 'Eliminar',
  'Apagando…': 'Eliminando…',
  'Apagar para sempre': 'Eliminar para siempre',
  Arquivar: 'Archivar',
  Reativar: 'Reactivar',
  Reconectar: 'Reconectar',
  Desconectar: 'Desconectar',
  Desconectada: 'Desconectada',
  Conectar: 'Conectar',
  'Validando…': 'Validando…',
  Criar: 'Crear',
  Editar: 'Editar',
  Fechar: 'Cerrar',
  Anterior: 'Anterior',
  Próxima: 'Siguiente',
  Anteriores: 'Anteriores',
  Seguintes: 'Siguientes',
  'página {n}': 'página {n}',
  Marcar: 'Marcar',
  Tirar: 'Quitar',
  limpar: 'limpiar',
  Limpar: 'Limpiar',
  'Limpar filtros': 'Limpiar filtros',
  'Limpar tudo': 'Limpiar todo',
  Filtros: 'Filtros',
  Atividade: 'Actividad',
  Status: 'Estado',
  Todas: 'Todas',
  Todos: 'Todos',
  Nenhum: 'Ninguno',
  Ninguém: 'Nadie',
  Alguém: 'Alguien',
  Conta: 'Cuenta',
  Tag: 'Etiqueta',
  tag: 'etiqueta',
  Etapa: 'Etapa',
  'Sem etapa': 'Sin etapa',
  'Todas as etapas': 'Todas las etapas',
  Responsável: 'Responsable',
  'Sem responsável': 'Sin responsable',
  'Qualquer um': 'Cualquiera',
  'Qualquer data': 'Cualquier fecha',
  Ativo: 'Activo',
  Inativo: 'Inactivo',
  Bloqueado: 'Bloqueado',
  'Bloqueou o bot': 'Bloqueó al bot',
  Premium: 'Premium',
  Token: 'Token',
  'Telegram ID': 'ID de Telegram',
  Telefone: 'Teléfono',
  De: 'Desde',
  Até: 'Hasta',
  até: 'hasta',
  '— escolher —': '— elegir —',
  '— não importar —': '— no importar —',
  '— nenhum registrado': '— ninguno registrado',
  'sem nome': 'sin nombre',
  'Digite {nome} para confirmar': 'Escribe {nome} para confirmar',
  'Digite {n} para confirmar': 'Escribe {n} para confirmar',
  'Ainda não confere com {nome}.': 'Todavía no coincide con {nome}.',
  'para sempre': 'para siempre',
  definitivamente: 'definitivamente',
  não: 'no',

  // ─── Lista de conversas (components/ConversationList.tsx) ──────────────────
  Conversas: 'Conversaciones',
  'Lista de conversas': 'Lista de conversaciones',
  'Buscar conversas': 'Buscar conversaciones',
  'Buscar nome, @usuário, mensagem…': 'Buscar nombre, @usuario, mensaje…',
  'Todas as contas': 'Todas las cuentas',
  '{n} conectada': '{n} conectada',
  '{n} conectadas': '{n} conectadas',
  'Silenciar som de mensagem nova': 'Silenciar el sonido de mensaje nuevo',
  'Ligar som de mensagem nova': 'Activar el sonido de mensaje nuevo',
  'Som ligado — clique para silenciar': 'Sonido activado — haz clic para silenciar',
  'Som silenciado': 'Sonido silenciado',
  'Ordenar conversas': 'Ordenar conversaciones',
  Ordenar: 'Ordenar',
  'Ações múltiplas': 'Acciones múltiples',
  'Mais recentes': 'Más recientes',
  'Última mensagem primeiro': 'El último mensaje primero',
  'Longa espera': 'Espera larga',
  'Quem aguarda resposta há mais tempo': 'Quien lleva más tiempo esperando respuesta',
  Destaque: 'Destacadas',
  'Favoritadas primeiro': 'Las destacadas primero',
  'Conversas abertas': 'Conversaciones abiertas',
  'Sem resposta': 'Sin respuesta',
  'Atribuídos a mim': 'Asignadas a mí',
  'Não lidas': 'No leídas',
  'Filtro personalizado': 'Filtro personalizado',
  'Filtros de conversa': 'Filtros de conversación',
  'Fechar filtros': 'Cerrar filtros',
  'Ver conversas': 'Ver conversaciones',
  'Última msg': 'Último mensaje',
  'Sem atribuição': 'Sin asignar',
  'Só conversas sem responsável': 'Solo conversaciones sin responsable',
  'Total: {n}': 'Total: {n}',
  'Carregando mais…': 'Cargando más…',
  'Nenhuma conversa por aqui.': 'No hay conversaciones por aquí.',
  'O CRM só enxerga mensagens recebidas a partir do momento em que a conta foi conectada — conversas antigas do Telegram não aparecem.':
    'El CRM solo ve los mensajes recibidos desde que se conectó la cuenta — las conversaciones antiguas de Telegram no aparecen.',
  'Marcar as conversas marcadas como respondidas':
    'Marcar como respondidas las conversaciones seleccionadas',
  'Marcar as conversas marcadas como não lidas':
    'Marcar como no leídas las conversaciones seleccionadas',
  'Fechar as conversas marcadas': 'Cerrar las conversaciones seleccionadas',
  Respondida: 'Respondida',
  'Não lida': 'No leída',
  'Sair da seleção múltipla': 'Salir de la selección múltiple',
  'Marcar todas as conversas carregadas': 'Marcar todas las conversaciones cargadas',
  'Ações da conversa': 'Acciones de la conversación',
  Ações: 'Acciones',
  'Marcar como respondida': 'Marcar como respondida',
  'Marcar como não lida': 'Marcar como no leída',
  'Fechar conversa': 'Cerrar conversación',
  'Abrir conversa com {nome}': 'Abrir conversación con {nome}',
  'Marcar conversa com {nome}': 'Seleccionar la conversación con {nome}',
  'Destacar {nome}': 'Destacar a {nome}',
  'Tirar {nome} do destaque': 'Quitar a {nome} de destacadas',
  Destacar: 'Destacar',
  'Tirar do destaque': 'Quitar de destacadas',
  'Atribuído a {nome}': 'Asignada a {nome}',
  'não lidas': 'no leídas',
  'Marcando mais de um responsável, aparecem as conversas de qualquer um deles.':
    'Al marcar más de un responsable, aparecen las conversaciones de cualquiera de ellos.',
  'Resolvido e Fechado ficam de fora até você marcá-los — a lista é fila de trabalho. A busca alcança os dois.':
    'Resuelto y Cerrado quedan fuera hasta que los marques — la lista es una cola de trabajo. La búsqueda sí los alcanza.',
  '{n} conta(s)': '{n} cuenta(s)',
  '{n} tag(s)': '{n} etiqueta(s)',
  'Áudio · {duracao}': 'Audio · {duracao}',

  // ─── Etapas do funil (nomes que nascem com o workspace) ────────────────────
  Aberto: 'Abierto',
  'Em atendimento': 'En atención',
  Aguardando: 'Esperando',
  Resolvido: 'Resuelto',
  Fechado: 'Cerrado',

  // ─── Conversa (components/MessageThread.tsx, Composer.tsx) ─────────────────
  'Selecione uma conversa': 'Selecciona una conversación',
  'O CRM mostra apenas mensagens recebidas depois que a conta do Telegram foi conectada. Conversas anteriores não são importadas — o Telegram não dá acesso ao histórico.':
    'El CRM muestra solo los mensajes recibidos después de conectar la cuenta de Telegram. Las conversaciones anteriores no se importan — Telegram no da acceso al historial.',
  'Voltar para a lista': 'Volver a la lista',
  'Perfil do lead': 'Perfil del contacto',
  'Fechar perfil': 'Cerrar perfil',
  'via {conta}': 'vía {conta}',
  'Carregar mensagens anteriores': 'Cargar mensajes anteriores',
  'Nenhuma mensagem nesta conversa.': 'No hay mensajes en esta conversación.',
  '{n} não lida': '{n} no leída',
  '{n} não lidas': '{n} no leídas',
  Cliente: 'Cliente',
  Você: 'Tú',
  'Fechar imagem': 'Cerrar imagen',
  'Abrir imagem em tela cheia': 'Abrir la imagen en pantalla completa',
  'Imagem recebida': 'Imagen recibida',
  Imagem: 'Imagen',
  'Baixar arquivo': 'Descargar archivo',
  'Identificador do link que trouxe este contato':
    'Identificador del enlace que trajo a este contacto',
  'Nenhuma campanha nem automação usa este identificador':
    'Ninguna campaña ni automatización usa este identificador',
  'sem cadastro': 'sin registrar',
  Inferida: 'Inferida',
  'Enviada pelo Telegram': 'Enviada desde Telegram',
  'Mensagem automática': 'Mensaje automático',
  Automação: 'Automatización',
  Campanha: 'Campaña',
  'Mensagem apagada': 'Mensaje eliminado',
  editada: 'editada',
  'Nota interna · visível só para a equipe': 'Nota interna · visible solo para el equipo',
  'Clique para tirar a reação': 'Haz clic para quitar la reacción',
  'Tirar a reação {emoji}': 'Quitar la reacción {emoji}',
  'Trocar a reação': 'Cambiar la reacción',
  'Trocar a reação {emoji}': 'Cambiar la reacción {emoji}',
  Reagir: 'Reaccionar',
  'Reagir a esta mensagem': 'Reaccionar a este mensaje',
  'Reagir com {emoji}': 'Reaccionar con {emoji}',
  Responder: 'Responder',
  'Responder esta mensagem': 'Responder este mensaje',
  'Tipo de mensagem ainda não suportado pelo CRM': 'Tipo de mensaje que el CRM todavía no admite',
  '{tipo} acima de 20 MB. O Telegram não libera arquivos desse tamanho para o CRM; peça ao contato para enviar um arquivo menor.':
    '{tipo} de más de 20 MB. Telegram no permite que el CRM descargue archivos de ese tamaño; pídele al contacto que envíe un archivo más liviano.',
  'Não foi possível baixar este {tipo}.': 'No se pudo descargar este {tipo}.',
  '{tipo} · baixando…': '{tipo} · descargando…',
  Enviando: 'Enviando',
  Entregue: 'Entregado',
  'Falha no envio': 'Falló el envío',
  falhou: 'falló',
  'Pausar áudio': 'Pausar el audio',
  'Reproduzir áudio de {n} segundos': 'Reproducir el audio de {n} segundos',
  'Avançar no áudio': 'Avanzar en el audio',
  'Velocidade de reprodução': 'Velocidad de reproducción',
  Transcrever: 'Transcribir',
  'A formatação chega ao cliente no Telegram': 'El formato le llega al cliente en Telegram',
  Agendar: 'Programar',
  Nova: 'Nueva',
  'Carregando mensagens…': 'Cargando mensajes…',
  'Carregando perfil…': 'Cargando perfil…',
  'Transcrever — em breve': 'Transcribir — próximamente',
  'Carregando perfil do lead': 'Cargando el perfil del contacto',
  Agendada: 'Programada',
  'hoje, {hora}': 'hoy, {hora}',
  'amanhã, {hora}': 'mañana, {hora}',
  'Editar agendamento — em breve': 'Editar la programación — próximamente',
  'Cancelar envio': 'Cancelar el envío',

  // ─── Campo de mensagem (components/Composer.tsx) ───────────────────────────
  'Escreva uma mensagem…': 'Escribe un mensaje…',
  'Anotação para a equipe…': 'Anotación para el equipo…',
  'respostas rápidas': 'respuestas rápidas',
  enviar: 'enviar',
  'nova linha': 'línea nueva',
  '↑↓ navegar · Enter inserir · Esc fechar': '↑↓ navegar · Enter insertar · Esc cerrar',
  'Com anexo': 'Con adjunto',
  '{n} anexos': '{n} adjuntos',
  'Esta nota {nao} será enviada ao cliente.': 'Esta nota {nao} se le envía al cliente.',
  'Esta conexão não concede o direito de responder. Peça ao dono da conta para habilitar “Responder mensagens” nas configurações de Chatbots do Telegram.':
    'Esta conexión no concede el permiso de responder. Pídele al dueño de la cuenta que active “Responder mensajes” en la configuración de Chatbots de Telegram.',
  'Respondendo o cliente': 'Respondiendo al cliente',
  'Respondendo você mesmo': 'Respondiendo a ti mismo',
  'Cancelar resposta': 'Cancelar la respuesta',
  'Remover anexo': 'Quitar el adjunto',
  'Anexar arquivo': 'Adjuntar archivo',
  'Inserir emoji': 'Insertar emoji',
  Formatação: 'Formato',
  'Negrito, itálico, link…': 'Negrita, cursiva, enlace…',
  'Respostas rápidas (ou digite / no campo)': 'Respuestas rápidas (o escribe / en el campo)',
  'Nenhuma resposta rápida cadastrada': 'No hay respuestas rápidas registradas',
  'Agendar envio': 'Programar el envío',
  'Escreva a mensagem antes de agendar': 'Escribe el mensaje antes de programarlo',
  'Escolher data e hora': 'Elegir fecha y hora',
  'Em 1 hora': 'En 1 hora',
  'Em 3 horas': 'En 3 horas',
  'Amanhã, 9h': 'Mañana, 9:00',
  'Gravar áudio': 'Grabar audio',
  'Gravar áudio — clique de novo para enviar': 'Grabar audio — haz clic de nuevo para enviar',
  'Este navegador não grava áudio': 'Este navegador no graba audio',
  'Descartar gravação': 'Descartar la grabación',
  'Enviar áudio': 'Enviar el audio',
  Enviar: 'Enviar',
  'Enviar mensagem': 'Enviar el mensaje',
  'Salvar nota': 'Guardar la nota',
  'O navegador bloqueou o microfone. Libere o acesso e tente de novo.':
    'El navegador bloqueó el micrófono. Permite el acceso e inténtalo de nuevo.',
  'Não foi possível gravar áudio neste navegador.': 'No se pudo grabar audio en este navegador.',
  'Falha ao enviar o áudio': 'No se pudo enviar el audio',
  'Falha de rede ao enviar o áudio': 'Falla de red al enviar el audio',

  // ─── Controle do bot na conversa (components/ControleDoBot.tsx) ────────────
  'Automações {paradas} nesta conversa. Nenhum fluxo começa aqui até você reativar.':
    'Automatizaciones {paradas} en esta conversación. Ningún flujo parte acá hasta que las reactives.',
  paradas: 'detenidas',
  'Fluxo em andamento: ': 'Flujo en curso: ',
  'Fluxos em andamento: ': 'Flujos en curso: ',
  ' — esperando o relógio': ' — esperando el reloj',
  ' — esperando o cliente apertar um botão': ' — esperando que el cliente apriete un botón',
  ' — esperando o cliente responder': ' — esperando que el cliente responda',
  'Parar bot': 'Detener el bot',

  // ─── Painel do contato (components/LeadPanel.tsx) ──────────────────────────
  'Origem · Campanha': 'Origen · Campaña',
  'Casada pelo texto da primeira mensagem — o cliente pode ter editado antes de enviar':
    'Emparejada por el texto del primer mensaje — el cliente pudo haberlo editado antes de enviarlo',
  'Primeiro contato': 'Primer contacto',
  'Sem campanha identificada': 'Sin campaña identificada',
  'Também entrou por': 'También entró por',
  'Origens no /start': 'Orígenes en el /start',
  'Nenhuma casou com campanha cadastrada.': 'Ninguno coincide con una campaña registrada.',
  'Copiar telefone': 'Copiar el teléfono',
  'Copiar Telegram ID': 'Copiar el ID de Telegram',
  'Atribuído a': 'Asignado a',
  'Última atividade': 'Última actividad',
  'Campos personalizados': 'Campos personalizados',
  'Outras conversas': 'Otras conversaciones',
  'Esta pessoa também fala com a equipe por outra conta conectada.':
    'Esta persona también habla con el equipo por otra cuenta conectada.',
  'Dados pessoais': 'Datos personales',
  'Apagar tudo deste lead': 'Eliminar todo de este contacto',
  'Apaga {definitivamente} o cadastro, todas as conversas, mensagens e anexos desta pessoa — em todas as contas conectadas. Não há como desfazer.':
    'Elimina {definitivamente} el registro, todas las conversaciones, mensajes y adjuntos de esta persona — en todas las cuentas conectadas. No se puede deshacer.',
  'buscar ou criar tag': 'buscar o crear etiqueta',
  'Remover tag {tag}': 'Quitar la etiqueta {tag}',
  'Enter cria a tag "{tag}".': 'Enter crea la etiqueta "{tag}".',
  'Clique numa tag existente ou digite para criar outra.':
    'Haz clic en una etiqueta existente o escribe para crear otra.',
  'Digite e aperte Enter para criar a primeira tag.':
    'Escribe y aprieta Enter para crear la primera etiqueta.',
  'Nenhuma atividade registrada ainda.': 'Todavía no hay actividad registrada.',
  'O bot não consegue mais enviar mensagens para esta pessoa.':
    'El bot ya no puede enviarle mensajes a esta persona.',
  'O bot não consegue mais enviar mensagens para esta pessoa. Detectado em {quando}, numa tentativa de envio.':
    'El bot ya no puede enviarle mensajes a esta persona. Detectado el {quando}, en un intento de envío.',
  'Nenhuma tentativa de envio falhou até agora. O Telegram não avisa quando alguém bloqueia — só se descobre tentando.':
    'Ningún intento de envío ha fallado hasta ahora. Telegram no avisa cuando alguien bloquea — solo se descubre intentando.',

  // ─── Linha do tempo do contato (components/LeadPanel.tsx) ──────────────────
  'moveu para {etapa}': 'movió a {etapa}',
  'marcou com a tag {tag}': 'marcó con la etiqueta {tag}',
  'removeu a tag {tag}': 'quitó la etiqueta {tag}',
  'assumiu o atendimento': 'tomó la atención',
  'removeu o responsável': 'quitó al responsable',
  'escreveu uma nota interna': 'escribió una nota interna',
  'agendou uma mensagem': 'programó un mensaje',
  'cancelou uma mensagem agendada': 'canceló un mensaje programado',
  'boas-vindas enviadas automaticamente': 'bienvenida enviada automáticamente',
  'resposta automática — {regra}': 'respuesta automática — {regra}',
  'regra removida': 'regla eliminada',
  'conexão do canal mudou': 'cambió la conexión del canal',
  'cliente voltou a escrever — conversa reaberta e sem responsável':
    'el cliente volvió a escribir — conversación reabierta y sin responsable',
  'marcou a conversa como respondida': 'marcó la conversación como respondida',
  'marcou a conversa como não lida': 'marcó la conversación como no leída',
  'clicou no link {link}': 'hizo clic en el enlace {link}',
  'na automação {nome}': 'en la automatización {nome}',
  'na transmissão {nome}': 'en la difusión {nome}',
  'apertou "{botao}" {onde}': 'apretó "{botao}" {onde}',
  'atualizou campos do perfil': 'actualizó campos del perfil',

  // ─── Contatos (routes/_app/contatos.tsx) ───────────────────────────────────
  Importar: 'Importar',
  Exportar: 'Exportar',
  'Todo mundo que já escreveu para alguma conta conectada. Uma pessoa aparece uma vez só, mesmo falando com Comercial e Suporte.':
    'Todos los que ya le escribieron a alguna cuenta conectada. Una persona aparece una sola vez, aunque hable con Comercial y con Soporte.',
  'Buscar contato': 'Buscar contacto',
  'Buscar por nome, @usuário, telefone ou ID do Telegram':
    'Buscar por nombre, @usuario, teléfono o ID de Telegram',
  'Mostrando quem tem todas as tags marcadas.':
    'Mostrando a quienes tienen todas las etiquetas marcadas.',
  '7 dias': '7 días',
  '30 dias': '30 días',
  'Nenhum contato para essa busca.': 'Ningún contacto para esa búsqueda.',
  'Nenhum contato com esses filtros.': 'Ningún contacto con esos filtros.',
  'Nenhum contato ainda.': 'Todavía no hay contactos.',
  'Contatos aparecem conforme as mensagens chegam — o CRM não importa histórico.':
    'Los contactos aparecen a medida que llegan los mensajes — el CRM no importa el historial.',
  Importado: 'Importado',
  'Importado de {origem} em {quando}': 'Importado desde {origem} el {quando}',
  'Importado em {quando}': 'Importado el {quando}',
  'Marcar {nome}': 'Seleccionar a {nome}',
  'Paginação dos contatos': 'Paginación de los contactos',
  '{intervalo} de {n} contato': '{intervalo} de {n} contacto',
  '{intervalo} de {n} contatos': '{intervalo} de {n} contactos',

  // ─── Ações em massa (components/AcoesEmContatos.tsx) ───────────────────────
  'Marcar esta página': 'Seleccionar esta página',
  'Opções de seleção': 'Opciones de selección',
  'Esta página ({n})': 'Esta página ({n})',
  'Todos os {n} do filtro': 'Los {n} del filtro',
  'Marque contatos para agir sobre eles, ou use a setinha para pegar o recorte inteiro.':
    'Selecciona contactos para actuar sobre ellos, o usa la flechita para tomar el filtro completo.',
  '{n} selecionado': '{n} seleccionado',
  '{n} selecionados': '{n} seleccionados',
  'o filtro inteiro': 'el filtro completo',
  'Editar tags': 'Editar etiquetas',
  Reatribuir: 'Reasignar',
  'Vale para {n} contato — o filtro inteiro, e não só a página.':
    'Aplica a {n} contacto — el filtro completo, no solo la página.',
  'Vale para {n} contato que você marcou.': 'Aplica a {n} contacto que seleccionaste.',
  'Vale para {n} contatos — o filtro inteiro, e não só a página.':
    'Aplica a {n} contactos — el filtro completo, no solo la página.',
  'Vale para {n} contatos que você marcou.': 'Aplica a {n} contactos que seleccionaste.',
  'Marque o que adicionar e o que tirar. O que ficar sem escolha não é tocado.':
    'Marca lo que agregar y lo que quitar. Lo que quede sin elección no se toca.',
  'Nenhuma tag ainda — crie uma na tela de Tags.':
    'Todavía no hay etiquetas — crea una en la pantalla de Etiquetas.',
  'O responsável é das conversas desses contatos — a mesma pessoa pode ter conversas em inboxes diferentes.':
    'El responsable es de las conversaciones de esos contactos — la misma persona puede tener conversaciones en bandejas distintas.',
  'Apagar contatos': 'Eliminar contactos',
  'Os contatos somem com todas as conversas e mensagens deles. Não há como desfazer.':
    'Los contactos desaparecen con todas sus conversaciones y mensajes. No se puede deshacer.',

  // ─── Importação e exportação ───────────────────────────────────────────────
  'Importar contatos': 'Importar contactos',
  'Um arquivo CSV com os contatos do outro CRM. Nada é criado até você conferir o mapeamento.':
    'Un archivo CSV con los contactos del otro CRM. No se crea nada hasta que revises el mapeo.',
  'O arquivo precisa de um cabeçalho e ao menos uma linha.':
    'El archivo necesita un encabezado y al menos una fila.',
  'Escolher arquivo CSV': 'Elegir archivo CSV',
  'Precisa ter o ID do Telegram de cada contato — é por ele que o CRM reconhece a pessoa.':
    'Tiene que traer el ID de Telegram de cada contacto — es por ahí que el CRM reconoce a la persona.',
  '{n} linha': '{n} fila',
  '{n} linhas': '{n} filas',
  'Qual coluna é o quê': 'Qué es cada columna',
  'coluna {n}': 'columna {n}',
  'Como vai ficar': 'Cómo va a quedar',
  '{n} linha sem ID do Telegram válido — não entra.':
    '{n} fila sin ID de Telegram válido — no entra.',
  '{n} linhas sem ID do Telegram válido — não entram.':
    '{n} filas sin ID de Telegram válido — no entran.',
  'Para onde vão': 'Adónde van',
  'nome da tag': 'nombre de la etiqueta',
  'O bot precisa ser o mesmo que essas pessoas já usavam — o Telegram só entrega mensagem de quem elas iniciaram. A tag é o que permite desfazer esta importação depois, em Contatos.':
    'El bot tiene que ser el mismo que esas personas ya usaban — Telegram solo entrega mensajes de quien ellas iniciaron. La etiqueta es lo que permite deshacer esta importación después, en Contactos.',
  'Não importar quem está marcado como bloqueado no arquivo.':
    'No importar a quienes figuran como bloqueados en el archivo.',
  'Trocar arquivo': 'Cambiar archivo',
  'Importar {n} contato': 'Importar {n} contacto',
  'Importar {n} contatos': 'Importar {n} contactos',
  'Escolha para qual conta os contatos vão.': 'Elige a qué cuenta van los contactos.',
  'Nenhuma linha válida para importar.': 'No hay filas válidas para importar.',
  'Falha ao enviar o arquivo.': 'No se pudo enviar el archivo.',
  'Enviando o arquivo': 'Enviando el archivo',
  'Pode demorar em arquivos grandes. Não feche esta janela.':
    'Puede demorar en archivos grandes. No cierres esta ventana.',
  Importando: 'Importando',
  'Importação concluída': 'Importación terminada',
  'Pode fechar — os contatos já estão em Contatos.':
    'Puedes cerrar — los contactos ya están en Contactos.',
  'Pode fechar esta janela: o trabalho continua no servidor.':
    'Puedes cerrar esta ventana: el trabajo sigue en el servidor.',
  '{n} linha(s) puladas — sem ID válido ou bloqueadas, conforme a opção marcada.':
    '{n} fila(s) saltadas — sin ID válido o bloqueadas, según la opción marcada.',
  'A importação falhou.': 'La importación falló.',
  '{feito} de {total} · {pct}%': '{feito} de {total} · {pct}%',
  'Exportar contatos': 'Exportar contactos',
  'Um CSV com nome, usuário, status, tags e datas. Os filtros já vêm com o que está aplicado na tela.':
    'Un CSV con nombre, usuario, estado, etiquetas y fechas. Los filtros vienen con lo que ya está aplicado en la pantalla.',
  'O período é por {campo}, o mesmo campo que a lista mostra na ponta direita de cada linha. Sem datas, vai tudo.':
    'El período es por {campo}, el mismo campo que la lista muestra al extremo derecho de cada fila. Sin fechas, va todo.',
  'última atividade': 'última actividad',
  'Baixar CSV': 'Descargar CSV',

  // ─── Trabalho em andamento (components/AvisoDeTrabalho.tsx) ────────────────
  'Importando contatos': 'Importando contactos',
  'Apagando contatos': 'Eliminando contactos',
  'Tirando tag dos contatos': 'Quitando la etiqueta de los contactos',
  'Trabalho em andamento': 'Trabajo en curso',

  // ─── Contas do Telegram (routes/_app/contas.tsx) ───────────────────────────
  'Conectar e configurar contas do Telegram é coisa de administrador. Fale com quem administra o workspace.':
    'Conectar y configurar cuentas de Telegram es cosa de administradores. Habla con quien administra el workspace.',
  'Bots do Telegram': 'Bots de Telegram',
  'Crie um bot no {botfather}, ative o {business} em Bot Settings e cole o token abaixo. O token é cifrado antes de ser gravado e nunca é exibido de volta.':
    'Crea un bot en {botfather}, activa el {business} en Bot Settings y pega el token acá abajo. El token se cifra antes de guardarse y nunca se muestra de vuelta.',
  'O {business} não está ligado neste bot. Sem ele, o Telegram não permite adicioná-lo a uma conta Business. Ative em @BotFather → /mybots → Bot Settings → Business Mode.':
    'El {business} no está activado en este bot. Sin él, Telegram no permite agregarlo a una cuenta Business. Actívalo en @BotFather → /mybots → Bot Settings → Business Mode.',
  'Bot cadastrado, mas o webhook {nao} foi registrado: o endereço público configurado não é alcançável pelo Telegram. Em desenvolvimento isso é esperado — use um túnel HTTPS para testar de ponta a ponta.':
    'El bot quedó registrado, pero el webhook {nao} se registró: la dirección pública configurada no es alcanzable desde Telegram. En desarrollo esto es lo esperado — usa un túnel HTTPS para probar de punta a punta.',
  'Bot conectado. Agora, no Telegram: {caminho}, adicione {arroba} e conceda as permissões.':
    'Bot conectado. Ahora, en Telegram: {caminho}, agrega {arroba} y concede los permisos.',
  'Configurações → Telegram Business → Chatbots': 'Configuración → Telegram Business → Chatbots',
  '{n} conta': '{n} cuenta',
  '{n} contas': '{n} cuentas',
  'Ative em @BotFather → /mybots → Bot Settings → Business Mode':
    'Actívalo en @BotFather → /mybots → Bot Settings → Business Mode',
  'Não consegui consultar o webhook de @{bot}:': 'No pude consultar el webhook de @{bot}:',
  'precisa de atenção': 'necesita atención',
  'tudo certo': 'todo en orden',
  'Atualizando…': 'Actualizando…',
  'Atualizar perfil': 'Actualizar el perfil',
  'Reaplicando…': 'Reaplicando…',
  'Reaplicar webhook': 'Reaplicar el webhook',
  Endereço: 'Dirección',
  'Na fila': 'En la cola',
  '{n} update(s) aguardando entrega': '{n} actualización(es) esperando entrega',
  'Último erro': 'Último error',
  'já recuperado': 'ya recuperado',
  Tipos: 'Tipos',
  'todos os que o CRM precisa': 'todos los que el CRM necesita',
  'faltando: {lista}': 'faltan: {lista}',
  'Sem {tipo} o CRM nunca fica sabendo que a conta foi conectada, e toda mensagem dela é descartada. Reaplique o webhook e depois remova e adicione o bot de novo em Telegram → Configurações → Telegram Business → Chatbots.':
    'Sin {tipo} el CRM nunca se entera de que la cuenta fue conectada, y todos sus mensajes se descartan. Reaplica el webhook y después quita y vuelve a agregar el bot en Telegram → Configuración → Telegram Business → Chatbots.',
  'Adicionar canal de bot': 'Agregar canal de bot',
  'Para atender por um bot comum, em vez de conta business. O lead precisa dar {start} primeiro, mas a atribuição de campanha fica {exata} — o link não pode ser adulterado.':
    'Para atender por un bot común, en vez de una cuenta business. El contacto tiene que dar {start} primero, pero la atribución de campaña queda {exata} — el enlace no se puede adulterar.',
  exata: 'exacta',
  'Bot…': 'Bot…',
  'Inbox…': 'Bandeja…',
  'Nome do canal': 'Nombre del canal',
  'Criar canal': 'Crear canal',
  'Sem estes direitos o CRM não funciona': 'Sin estos permisos el CRM no funciona',
  Limitações: 'Limitaciones',
  'Todos os direitos concedidos': 'Todos los permisos concedidos',
  'Contas conectadas': 'Cuentas conectadas',
  'Cada conta atende por um inbox, que tem funil e equipe próprios. As contas business aparecem aqui automaticamente assim que o bot é adicionado nas configurações de Chatbots do Telegram. Trocar o inbox leva junto todo o histórico da conta e muda quem a atende.':
    'Cada cuenta atiende por una bandeja, que tiene su propio embudo y su propio equipo. Las cuentas business aparecen acá automáticamente apenas se agrega el bot en la configuración de Chatbots de Telegram. Cambiar la bandeja se lleva todo el historial de la cuenta y cambia quién la atiende.',
  'Nenhuma conta conectada ainda.': 'Todavía no hay cuentas conectadas.',
  Bot: 'Bot',
  'Conta business': 'Cuenta business',
  'Copiar link do bot': 'Copiar el enlace del bot',
  '{n} conversa': '{n} conversación',
  '{n} conversas': '{n} conversaciones',
  '{n} mensagem não lida': '{n} mensaje no leído',
  '{n} mensagens não lidas': '{n} mensajes no leídos',
  'conectada em {data}': 'conectada el {data}',
  'Remover “{nome}”': 'Eliminar “{nome}”',
  'A conta sai do CRM junto com tudo que depende dela. Não há como desfazer.':
    'La cuenta sale del CRM junto con todo lo que depende de ella. No se puede deshacer.',
  'Conferindo o que depende desta conta…': 'Revisando qué depende de esta cuenta…',
  'Isto apaga {sempre}:': 'Esto elimina {sempre}:',
  'Esta conta ainda não tem histórico — nada além dela mesma será apagado.':
    'Esta cuenta todavía no tiene historial — no se elimina nada más que ella misma.',
  'Apagar também {n} contato que fala só por esta conta. Desmarcado, ele continua em Contatos sem o histórico.':
    'Eliminar también {n} contacto que habla solo por esta cuenta. Sin marcar, sigue en Contactos pero sin el historial.',
  'Apagar também {n} contatos que falam só por esta conta. Desmarcados, eles continuam em Contatos sem o histórico.':
    'Eliminar también {n} contactos que hablan solo por esta cuenta. Sin marcar, siguen en Contactos pero sin el historial.',
  'Nenhum contato depende só desta conta — todos continuam em Contatos.':
    'Ningún contacto depende solo de esta cuenta — todos siguen en Contactos.',

  // ─── Direitos do canal (texto que nasce no servidor) ───────────────────────
  'Responder mensagens': 'Responder mensajes',
  'Sem isso o CRM só consegue ler; nenhum envio funciona.':
    'Sin esto el CRM solo puede leer; ningún envío funciona.',
  'Marcar como lida': 'Marcar como leída',
  'Sem isso, abrir a conversa no CRM não marca como lida no Telegram.':
    'Sin esto, abrir la conversación en el CRM no la marca como leída en Telegram.',
  'Apagar mensagens enviadas': 'Eliminar mensajes enviados',
  'Sem isso, apagar pelo CRM não remove a mensagem no Telegram.':
    'Sin esto, eliminar desde el CRM no borra el mensaje en Telegram.',

  // ─── Bandejas e equipe (components/InboxManager.tsx) ───────────────────────
  'inbox {nome}': 'bandeja {nome}',
  'Nome do inbox {nome}': 'Nombre de la bandeja {nome}',
  'Nome do novo inbox': 'Nombre de la bandeja nueva',
  'Criar inbox': 'Crear bandeja',
  'Apagar inbox': 'Eliminar bandeja',
  'Nenhum inbox ativo. Sem pelo menos um, mensagens que chegarem não terão onde entrar.':
    'No hay ninguna bandeja activa. Sin al menos una, los mensajes que lleguen no tendrán dónde entrar.',
  'O funil e a equipe deste inbox somem junto. Não há como desfazer.':
    'El embudo y el equipo de esta bandeja desaparecen junto con ella. No se puede deshacer.',
  'Arquivar esconde o inbox e dá para reverter. Apagar, não.':
    'Archivar esconde la bandeja y se puede revertir. Eliminar, no.',
  'Atribuição automática': 'Asignación automática',
  'Atribuição automática neste inbox': 'Asignación automática en esta bandeja',
  'Cada atendente vê só as conversas dele': 'Cada agente ve solo sus propias conversaciones',
  'Neste inbox, quem atende enxerga as conversas atribuídas a si e as que ainda não têm dono — as dos colegas somem da lista, da busca, dos contatos e dos relatórios. Dono e administradores continuam vendo tudo.':
    'En esta bandeja, quien atiende ve las conversaciones asignadas a sí mismo y las que todavía no tienen dueño — las de los colegas desaparecen de la lista, de la búsqueda, de los contactos y de los reportes. Dueño y administradores siguen viendo todo.',
  'Com a atribuição automática desligada, conversa nova nasce sem dono e fica visível para todos.':
    'Con la asignación automática apagada, una conversación nueva nace sin dueño y queda visible para todos.',
  'Conversa que chega sem dono é distribuída entre quem está ligado abaixo.':
    'Las conversaciones que llegan sin dueño se reparten entre quienes estén activados acá abajo.',
  'Distribuir por igual': 'Repartir por igual',
  'Ninguém neste inbox. Só dono e administradores enxergam as conversas.':
    'Nadie en esta bandeja. Solo el dueño y los administradores ven las conversaciones.',
  'Adicionar:': 'Agregar:',
  'Dono e administradores já enxergam todas as conversas. Vincular serve para entrarem na atribuição automática.':
    'El dueño y los administradores ya ven todas las conversaciones. Vincularlos sirve para que entren en la asignación automática.',

  // ─── Menus do cabeçalho (components/HeaderMenus.tsx) ───────────────────────
  'Etapa do atendimento': 'Etapa de la atención',
  Atribuir: 'Asignar',
  'Atribuir a um atendente': 'Asignar a un agente',

  // ─── Trilha administrativa (components/AuditLog.tsx) ───────────────────────
  'Nada registrado ainda. As ações administrativas aparecem aqui conforme acontecem.':
    'Todavía no hay nada registrado. Las acciones administrativas aparecen acá a medida que ocurren.',
  'removeu o bot {bot}': 'quitó el bot {bot}',
  'conectou a conta {nome}{arroba}': 'conectó la cuenta {nome}{arroba}',
  'apagou a conta {nome}': 'eliminó la cuenta {nome}',
  'apagou a conta {nome}, e {n} contato(s) junto':
    'eliminó la cuenta {nome}, y {n} contacto(s) junto con ella',
  'desconectou a conta {nome}': 'desconectó la cuenta {nome}',
  'reconectou a conta {nome}': 'reconectó la cuenta {nome}',
  'criou o inbox {nome}': 'creó la bandeja {nome}',
  'arquivou o inbox {nome}': 'archivó la bandeja {nome}',
  'reativou o inbox {nome}': 'reactivó la bandeja {nome}',
  'apagou o inbox {nome}': 'eliminó la bandeja {nome}',
  'criou um convite como {papel}': 'creó una invitación como {papel}',
  'criou um convite para {rotulo} como {papel}': 'creó una invitación para {rotulo} como {papel}',
  'revogou um convite': 'revocó una invitación',
  'apagou a campanha {nome}': 'eliminó la campaña {nome}',
  'entrou no workspace pelo convite, como {papel}':
    'entró al workspace por la invitación, como {papel}',
  'mudou o papel de {membro} para {papel}': 'cambió el rol de {membro} a {papel}',
  'removeu {membro} do workspace': 'quitó a {membro} del workspace',
  'deu a {membro} acesso ao inbox {inbox}': 'le dio a {membro} acceso a la bandeja {inbox}',
  'tirou de {membro} o acesso ao inbox {inbox}':
    'le quitó a {membro} el acceso a la bandeja {inbox}',
  'apagou o contato {quem} (ID {id}), com {conversas} conversa(s) e {mensagens} mensagem(ns)':
    'eliminó el contacto {quem} (ID {id}), con {conversas} conversación(es) y {mensagens} mensaje(s)',
  'criou a campanha {nome}': 'creó la campaña {nome}',
  'disparou a transmissão {nome}': 'lanzó la difusión {nome}',

  // ─── Login e convite (routes/login.tsx, convite.$token.tsx) ────────────────────
  'CRM para Telegram': 'CRM para Telegram',
  Convite: 'Invitación',
  'Crie sua conta para entrar no workspace. Já tem conta?':
    'Crea tu cuenta para entrar al workspace. ¿Ya tienes una?',
  'Entrar com ela': 'Entrar con ella',
  'Criar uma nova': 'Crear una nueva',
  'Primeiro acesso': 'Primer acceso',
  'Ninguém entrou aqui ainda. Esta primeira conta vira a dona do workspace e é quem convida o resto da equipe.':
    'Todavía no ha entrado nadie. Esta primera cuenta pasa a ser la dueña del workspace y es quien invita al resto del equipo.',
  Nome: 'Nombre',
  'E-mail': 'Correo',
  Senha: 'Contraseña',
  'Mínimo de 10 caracteres': 'Mínimo 10 caracteres',
  'Aguarde…': 'Espera…',
  Entrar: 'Entrar',
  'Criar conta': 'Crear cuenta',
  'O acesso é por convite. Se ainda não tem conta, peça um link a quem administra o workspace.':
    'El acceso es por invitación. Si todavía no tienes cuenta, pídele un enlace a quien administra el workspace.',
  'E-mail ou senha incorretos': 'Correo o contraseña incorrectos',
  'Já existe uma conta com este e-mail': 'Ya existe una cuenta con este correo',
  'A senha precisa ter ao menos 10 caracteres': 'La contraseña necesita al menos 10 caracteres',
  'Não foi possível entrar': 'No se pudo entrar',
  'Não foi possível criar a conta': 'No se pudo crear la cuenta',
  'Verificando convite…': 'Verificando la invitación…',
  'Convite indisponível': 'Invitación no disponible',
  'Este link de convite não existe.': 'Este enlace de invitación no existe.',
  'Este convite foi revogado por quem o criou.': 'Esta invitación fue revocada por quien la creó.',
  'Este convite expirou.': 'Esta invitación expiró.',
  'Este convite já atingiu o limite de usos.': 'Esta invitación ya llegó al límite de usos.',
  'Este convite não pode ser usado.': 'Esta invitación no se puede usar.',
  'Peça um link novo a quem administra o workspace.':
    'Pídele un enlace nuevo a quien administra el workspace.',
  'Redirecionando para o login…': 'Redirigiendo al inicio de sesión…',
  'Você foi convidado como {papel}.': 'Te invitaron como {papel}.',
  administrador: 'administrador',
  'Entrando…': 'Entrando…',
  'Tentar de novo': 'Intentar de nuevo',
  'Entrando como {email}': 'Entrando como {email}',

  // ─── Agendados (routes/_app/agendados.tsx) ─────────────────────────────────────
  'Tudo que ainda vai sair, de todas as contas. O CRM confere de minuto em minuto, então o envio pode atrasar até um minuto do horário marcado.':
    'Todo lo que todavía va a salir, de todas las cuentas. El CRM revisa minuto a minuto, así que el envío puede atrasarse hasta un minuto del horario marcado.',
  Prazo: 'Plazo',
  'Tudo que vem': 'Todo lo que viene',
  'Até amanhã': 'Hasta mañana',
  'Próximos 7 dias': 'Próximos 7 días',
  'Nada agendado nesse recorte.': 'No hay nada programado en ese filtro.',
  'Nenhuma mensagem agendada.': 'No hay mensajes programados.',
  'Use o relógio no campo de mensagem para agendar um envio.':
    'Usa el reloj en el campo de mensaje para programar un envío.',
  'Agendada por {nome}': 'Programada por {nome}',

  // ─── Respostas rápidas (routes/_app/respostas.tsx) ─────────────────────────────
  'Nova resposta': 'Respuesta nueva',
  'Digite {barra} no campo de mensagem para abrir a lista, ou use o botão ao lado do anexo. O atalho não pode ter espaços.':
    'Escribe {barra} en el campo de mensaje para abrir la lista, o usa el botón al lado del adjunto. El atajo no puede llevar espacios.',
  'Nova resposta rápida': 'Respuesta rápida nueva',
  'O atalho é o que se digita depois da barra no campo de mensagem.':
    'El atajo es lo que se escribe después de la barra en el campo de mensaje.',
  Atalho: 'Atajo',
  proposta: 'propuesta',
  Título: 'Título',
  'Enviar proposta padrão': 'Enviar la propuesta estándar',
  Texto: 'Texto',
  'Segue nossa proposta padrão. Qualquer dúvida sobre os planos, me chama.':
    'Te va nuestra propuesta estándar. Cualquier duda sobre los planes, me escribes.',
  'Onde vale': 'Dónde aplica',
  'Todos os inboxes': 'Todas las bandejas',
  'Só em {nome}': 'Solo en {nome}',
  'Anexo (opcional)': 'Adjunto (opcional)',
  'Anexos (opcional)': 'Adjuntos (opcional)',
  'vídeo circular': 'video circular',
  'Enviando…': 'Enviando…',
  'Anexar imagem ou arquivo': 'Adjuntar imagen o archivo',
  'Anexar mais': 'Adjuntar más',
  'Um álbum leva no máximo {n} mídias.': 'Un álbum admite {n} archivos como máximo.',
  'Com dois ou mais anexos a resposta chega como um álbum: as mídias aparecem juntas, numa grade, e o texto vira a legenda da primeira.':
    'Con dos o más adjuntos la respuesta llega como un álbum: los archivos aparecen juntos, en una grilla, y el texto pasa a ser la descripción del primero.',
  'Vídeo circular (nota de vídeo)': 'Video circular (nota de video)',
  'O arquivo sobe uma vez e vai junto toda vez que a resposta for usada.':
    'El archivo se sube una vez y va adjunto cada vez que se use la respuesta.',
  'Vídeo circular não tem legenda no Telegram. O vídeo chega primeiro e o texto da resposta vem logo depois, numa mensagem própria.':
    'El video circular no acepta descripción en Telegram. El video llega primero y el texto de la respuesta viene justo después, en un mensaje aparte.',
  Adicionar: 'Agregar',
  'Nenhuma resposta rápida ainda. Crie a primeira em “Nova resposta”.':
    'Todavía no hay respuestas rápidas. Crea la primera en “Respuesta nueva”.',
  'Um inbox': 'Una bandeja',
  anexo: 'adjunto',
  'Falha ao enviar o arquivo': 'No se pudo enviar el archivo',
  'Falha de rede ao enviar o arquivo': 'Falla de red al enviar el archivo',

  // ─── Tags (routes/_app/tags.tsx) ───────────────────────────────────────────────
  'Nova tag': 'Etiqueta nueva',
  'As tags são do workspace inteiro: renomear ou trocar a cor muda em todos os leads marcados.':
    'Las etiquetas son de todo el workspace: renombrarlas o cambiarles el color afecta a todos los contactos marcados.',
  'Apagar tira a marca de todos eles — o lead continua onde está.':
    'Eliminarla les quita la marca a todos — el contacto sigue donde está.',
  'da nova tag': 'de la etiqueta nueva',
  'Nome da tag': 'Nombre de la etiqueta',
  'A cor já vem sugerida pela paleta; trocar aqui ou depois, na lista, dá no mesmo.':
    'El color viene sugerido por la paleta; cambiarlo acá o después, en la lista, da lo mismo.',
  'tag {nome}': 'etiqueta {nome}',
  '{n} lead': '{n} contacto',
  '{n} leads': '{n} contactos',
  Renomear: 'Renombrar',
  'Apagar de {n}': 'Eliminar de {n}',
  'Apagar os contatos de “{nome}”': 'Eliminar los contactos de “{nome}”',
  Concluído: 'Terminado',
  Apagando: 'Eliminando',
  '{n} apagados': '{n} eliminados',
  'Pode fechar.': 'Puedes cerrar.',
  'Isto apaga {n} contato e todo o histórico de conversa dele.':
    'Esto elimina {n} contacto y todo su historial de conversación.',
  'Isto apaga {n} contatos e todo o histórico de conversa deles.':
    'Esto elimina {n} contactos y todo su historial de conversación.',

  // ─── Relatórios e gráficos (routes/_app/relatorios.tsx, components/charts.tsx) ───
  'Ainda não há o que medir.': 'Todavía no hay nada que medir.',
  'Os números aparecem conforme as conversas chegam. O CRM não importa histórico do Telegram, então o relatório começa do zero junto com a primeira conta conectada.':
    'Los números aparecen a medida que llegan las conversaciones. El CRM no importa el historial de Telegram, así que el reporte parte de cero junto con la primera cuenta conectada.',
  'Em aberto': 'Abiertas',
  'Fora das etapas finais': 'Fuera de las etapas finales',
  'Abertas e sem ninguém atribuído': 'Abiertas y sin nadie asignado',
  'Primeira resposta': 'Primera respuesta',
  'Mediana de {n} conversa(s) · 90% em até {p90}':
    'Mediana de {n} conversación(es) · 90% en hasta {p90}',
  'Nenhuma conversa respondida no período': 'Ninguna conversación respondida en el período',
  '{n} conversa(s) do período nunca receberam resposta. Elas ficam fora da mediana de propósito — contá-las como zero esconderia justamente o problema.':
    '{n} conversación(es) del período nunca recibieron respuesta. Quedan fuera de la mediana a propósito — contarlas como cero escondería justamente el problema.',
  'Carga por atendente': 'Carga por agente',
  'Número à direita: conversas em aberto na mão do agente.':
    'El número a la derecha: conversaciones abiertas en manos del agente.',
  'Nenhuma conversa atribuída.': 'No hay conversaciones asignadas.',
  'Origem por campanha': 'Origen por campaña',
  'Nenhuma campanha cadastrada.': 'No hay campañas registradas.',
  Leads: 'Contactos',
  Exatos: 'Exactos',
  'Já existentes': 'Ya existentes',
  Concluídos: 'Terminados',
  inferida: 'inferida',
  '{exatos} são os leads que chegaram pelo link de {start}, que o cliente não consegue alterar. O restante veio de conta business, onde a origem é {inferida} pelo texto da primeira mensagem e pode errar. {jaExistentes} são os que chegaram por esta campanha mas já estavam no CRM: a origem de um lead é a primeira e não muda, então eles não entram na coluna de leads.':
    '{exatos} son los contactos que llegaron por el enlace de {start}, que el cliente no puede alterar. El resto vino de una cuenta business, donde el origen es {inferida} por el texto del primer mensaje y puede equivocarse. {jaExistentes} son los que llegaron por esta campaña pero ya estaban en el CRM: el origen de un contacto es el primero y no cambia, así que no entran en la columna de contactos.',
  '{n} s': '{n} s',
  '{n} min': '{n} min',
  '{h} h': '{h} h',
  '{h} h {m} min': '{h} h {m} min',
  'Mensagens por dia': 'Mensajes por día',
  Recebidas: 'Recibidos',
  Enviadas: 'Enviados',
  'Ver gráfico': 'Ver gráfico',
  'Ver tabela': 'Ver tabla',
  Dia: 'Día',
  'Mensagens recebidas e enviadas por dia': 'Mensajes recibidos y enviados por día',
  'Conversas por etapa': 'Conversaciones por etapa',

  // ─── Campanhas (routes/_app/campanhas.tsx) ─────────────────────────────────────
  'Nova campanha': 'Campaña nueva',
  'Cada campanha gera um link rastreável. Quando alguém chega por ele, a origem fica registrada no perfil do lead.':
    'Cada campaña genera un enlace rastreable. Cuando alguien llega por ahí, el origen queda registrado en el perfil del contacto.',
  'O link gerado leva direto para a conta escolhida e registra a origem no perfil de quem chegar por ele.':
    'El enlace generado lleva directo a la cuenta elegida y registra el origen en el perfil de quien llegue por él.',
  'Leads no período': 'Contactos en el período',
  'Novos e já existentes': 'Nuevos y ya existentes',
  Novos: 'Nuevos',
  'Primeiro contato veio por campanha': 'El primer contacto vino por campaña',
  'Atribuição exata': 'Atribución exacta',
  'Chegaram pelo link de /start': 'Llegaron por el enlace de /start',
  'Conversas em etapa final': 'Conversaciones en etapa final',
  'Buscar campanha': 'Buscar campaña',
  'Buscar campanha pelo nome ou identificador': 'Buscar campaña por nombre o identificador',
  Atribuição: 'Atribución',
  Qualquer: 'Cualquiera',
  'Nenhuma campanha com esses filtros.': 'Ninguna campaña con esos filtros.',
  'O identificador viaja invisível no /start — atribuição exata':
    'El identificador viaja invisible en el /start — atribución exacta',
  'Atribuição inferida pelo texto da primeira mensagem':
    'Atribución inferida por el texto del primer mensaje',
  Exata: 'Exacta',
  Aproximada: 'Aproximada',
  Inativa: 'Inactiva',
  'Chegaram por esta campanha já estando no CRM. A origem registrada continua sendo a primeira.':
    'Llegaron por esta campaña ya estando en el CRM. El origen registrado sigue siendo el primero.',
  'Os contatos somem do CRM com todas as conversas e mensagens deles. Não há como desfazer.':
    'Los contactos desaparecen del CRM con todas sus conversaciones y mensajes. No se puede deshacer.',
  'Todo mundo que chegou por esta campanha: os novos e os que já estavam no CRM':
    'Todos los que llegaron por esta campaña: los nuevos y los que ya estaban en el CRM',
  'Leads cuja origem registrada é esta campanha — o primeiro contato deles veio por ela':
    'Contactos cuyo origen registrado es esta campaña — su primer contacto vino por ella',
  '{n} novo': '{n} nuevo',
  '{n} novos': '{n} nuevos',
  '{n} já existente': '{n} ya existente',
  '{n} já existentes': '{n} ya existentes',
  '{n} exato': '{n} exacto',
  '{n} exatos': '{n} exactos',
  '{n} concluído': '{n} terminado',
  '{n} concluídos': '{n} terminados',
  '{n} lead no período': '{n} contacto en el período',
  '{n} leads no período': '{n} contactos en el período',
  '{n} lead(s) perdem a origem registrada': '{n} contacto(s) pierden el origen registrado',
  'Nenhum lead veio por esta campanha': 'Ningún contacto vino por esta campaña',
  'Apagar e soltar {n} lead(s)?': '¿Eliminar y soltar {n} contacto(s)?',
  Confirmar: 'Confirmar',
  'Nenhuma campanha ainda — crie a primeira acima.':
    'Todavía no hay campañas — crea la primera acá arriba.',
  'Nenhuma campanha ainda. Crie a primeira em “Nova campanha”.':
    'Todavía no hay campañas. Crea la primera en “Campaña nueva”.',
  'Selecione…': 'Elige…',
  'Como aparece no CRM': 'Como aparece en el CRM',
  'ODD 200': 'ODD 200',
  Identificador: 'Identificador',
  'Letras, números, _ e - (até 64)': 'Letras, números, _ y - (hasta 64)',
  odd200: 'odd200',
  'Texto pré-preenchido': 'Texto precargado',
  'O cliente vê este texto no campo de mensagem — e pode editá-lo antes de enviar, então a atribuição é aproximada.':
    'El cliente ve este texto en el campo de mensaje — y puede editarlo antes de enviarlo, así que la atribución es aproximada.',
  'Quero saber da ODD 200': 'Quiero saber de la ODD 200',
  'Criar campanha': 'Crear campaña',

  // ─── Transmissão (routes/_app/transmissao.tsx) ─────────────────────────────────
  'Nova transmissão': 'Difusión nueva',
  'Envia a mesma mensagem para um recorte de conversas, por uma conta conectada. O Telegram só entrega para quem já falou com essa conta — não existe importar lista.':
    'Envía el mismo mensaje a un grupo de conversaciones, por una cuenta conectada. Telegram solo entrega a quien ya habló con esa cuenta — no existe importar una lista.',
  'O Telegram só entrega para quem já falou com a conta escolhida — não existe importar lista.':
    'Telegram solo entrega a quien ya habló con la cuenta elegida — no existe importar una lista.',
  'Quem recebe': 'Quién recibe',
  'Os mesmos filtros da Inbox. O número ao lado é quanta gente o recorte alcança agora.':
    'Los mismos filtros de la Bandeja. El número al lado es a cuánta gente alcanza el filtro ahora.',
  'Conta que envia': 'Cuenta que envía',
  'Escolha a conta…': 'Elige la cuenta…',
  'Só quem está sem resposta': 'Solo quienes están sin respuesta',
  'Só com mensagem não lida': 'Solo con mensajes no leídos',
  'Incluir conversas fechadas': 'Incluir conversaciones cerradas',
  '{n} conversa recebe esta transmissão.': '{n} conversación recibe esta difusión.',
  '{n} conversas recebem esta transmissão.': '{n} conversaciones reciben esta difusión.',
  'Nome interno': 'Nombre interno',
  'Promoção de setembro': 'Promoción de septiembre',
  Anexos: 'Adjuntos',
  'Subindo…': 'Subiendo…',
  'Anexar imagem, vídeo ou arquivo': 'Adjuntar imagen, video o archivo',
  'O vídeo circular vai sozinho — o Telegram não o agrupa com outros anexos':
    'El video circular va solo — Telegram no lo agrupa con otros adjuntos',
  'Vídeo circular não tem legenda no Telegram. O vídeo chega primeiro e o texto abaixo vem em seguida, numa mensagem própria, com os botões.':
    'El video circular no acepta descripción en Telegram. El video llega primero y el texto de abajo viene después, en un mensaje aparte, con los botones.',
  'Com mais de um arquivo, tudo vai como álbum numa mensagem só — e o Telegram não aceita botões em álbum.':
    'Con más de un archivo, todo va como álbum en un solo mensaje — y Telegram no acepta botones en un álbum.',
  'Oi {primeiro_nome}! Abrimos as vagas de setembro…':
    '¡Hola {primeiro_nome}! Abrimos los cupos de septiembre…',
  Botões: 'Botones',
  'Texto do botão': 'Texto del botón',
  'Chega como mensagem não lida do lead': 'Llega como mensaje no leído del contacto',
  'Remover botão': 'Quitar el botón',
  'botão de link': 'botón de enlace',
  'botão de resposta rápida': 'botón de respuesta rápida',
  'Rastrear cliques nos links': 'Rastrear los clics en los enlaces',
  'O botão sai com um link curto do CRM que redireciona ao destino e conta quem clicou.':
    'El botón sale con un enlace corto del CRM que redirige al destino y cuenta quién hizo clic.',
  'O botão sai com o endereço original, sem medir nada.':
    'El botón sale con la dirección original, sin medir nada.',
  'Agendar (opcional)': 'Programar (opcional)',
  'Bloquear encaminhar e salvar': 'Bloquear reenviar y guardar',
  'Preparando…': 'Preparando…',
  'Agendar transmissão': 'Programar la difusión',
  'Enviar para {n}': 'Enviar a {n}',
  'Sai no horário escolhido, no fuso do seu aparelho.':
    'Sale en el horario elegido, en la zona horaria de tu dispositivo.',
  'Começa a sair agora, em lotes, respeitando o limite do Telegram.':
    'Empieza a salir ahora, por tandas, respetando el límite de Telegram.',
  Transmissões: 'Difusiones',
  'Nenhuma transmissão ainda.': 'Todavía no hay difusiones.',
  Rascunho: 'Borrador',
  Pausada: 'Pausada',
  Concluída: 'Terminada',
  Cancelada: 'Cancelada',
  'Enviada por': 'Enviada por',
  'Criada por': 'Creada por',
  '{enviadas} de {total} enviadas': '{enviadas} de {total} enviadas',
  '{n} falhas': '{n} fallas',
  'sai {quando}': 'sale {quando}',
  'terminou {quando}': 'terminó {quando}',
  'Ver o que foi enviado': 'Ver lo que se envió',
  'O que foi enviado': 'Lo que se envió',
  'Sem texto — só o anexo.': 'Sin texto — solo el adjunto.',
  'Resposta rápida — chega como mensagem não lida do lead':
    'Respuesta rápida — llega como mensaje no leído del contacto',
  'Enviada sem permitir encaminhar ou salvar.': 'Enviada sin permitir reenviar ni guardar.',
  'Cliques por link': 'Clics por enlace',
  '{n} pessoa': '{n} persona',
  '{n} pessoas': '{n} personas',
  '{n} cliques': '{n} clics',
  'Dê um nome interno para reconhecer esta transmissão depois.':
    'Ponle un nombre interno para reconocer esta difusión después.',
  'Conta business não aceita botões — é limite do Telegram. Use um bot para transmissão com botão.':
    'Una cuenta business no acepta botones — es un límite de Telegram. Usa un bot para una difusión con botones.',
  'Álbum com várias mídias não aceita botões.': 'Un álbum con varios archivos no acepta botones.',
  'Última mensagem': 'Último mensaje',

  // ─── Automações — lista e modo simples (routes/_app/automacoes.index.tsx) ──────
  'Nova automação': 'Automatización nueva',
  'Respostas que saem sozinhas. Assim que alguém da equipe responde, as automações daquela conversa param — o robô não atropela atendimento em andamento.':
    'Respuestas que salen solas. Apenas alguien del equipo responde, las automatizaciones de esa conversación se detienen — el robot no atropella una atención en curso.',
  'Conecte uma conta do Telegram para criar automações.':
    'Conecta una cuenta de Telegram para crear automatizaciones.',
  'Nenhuma automação ainda. Crie a primeira em “Nova automação”.':
    'Todavía no hay automatizaciones. Crea la primera en “Automatización nueva”.',
  rascunho: 'borrador',
  'no ar': 'al aire',
  parado: 'detenida',
  '{contatos} contato(s) · {passagens} passagem(ns)':
    '{contatos} contacto(s) · {passagens} pasada(s)',
  'nunca rodou': 'nunca ha corrido',
  Parar: 'Detener',
  Publicar: 'Publicar',
  'Editar automação': 'Editar la automatización',
  bot: 'bot',
  'conta business': 'cuenta business',
  'Conta de bot: aceita botões e link de entrada.':
    'Cuenta de bot: acepta botones y enlace de entrada.',
  'Conta business: o Telegram não permite botão nem /start em nome dela. Um menu aqui é “responda 1, 2 ou 3”.':
    'Cuenta business: Telegram no permite botones ni /start en su nombre. Un menú acá es “responde 1, 2 o 3”.',
  'Boas-vindas do Comercial': 'Bienvenida de Comercial',
  'Quando disparar': 'Cuándo se dispara',
  Telegram: 'Telegram',
  'Eventos do contato': 'Eventos del contacto',
  'Primeira mensagem': 'Primer mensaje',
  'Dispara quando alguém escreve pela primeira vez nesta conversa.':
    'Se dispara cuando alguien escribe por primera vez en esta conversación.',
  'Entrou pelo link (/start)': 'Entró por el enlace (/start)',
  'Dispara para quem chega por um link de campanha, antes de escrever qualquer coisa.':
    'Se dispara para quien llega por un enlace de campaña, antes de escribir nada.',
  'Palavra-chave': 'Palabra clave',
  'Dispara quando a mensagem contém uma das palavras. Ignora acento e maiúscula.':
    'Se dispara cuando el mensaje contiene una de las palabras. Ignora tildes y mayúsculas.',
  'Fora do expediente': 'Fuera del horario de atención',
  'Dispara fora do horário de atendimento do inbox.':
    'Se dispara fuera del horario de atención de la bandeja.',
  'Contato criado': 'Contacto creado',
  'Dispara quando a pessoa entra no CRM — inclusive por importação. Diferente de “primeira mensagem”: aquele é por conversa, este é por pessoa, uma vez na vida.':
    'Se dispara cuando la persona entra al CRM — incluso por importación. Distinto de “primer mensaje”: aquel es por conversación, este es por persona, una vez en la vida.',
  'Tag adicionada': 'Etiqueta agregada',
  'Dispara quando a tag é adicionada ao contato, por alguém da equipe ou por outro fluxo.':
    'Se dispara cuando se le agrega la etiqueta al contacto, por alguien del equipo o por otro flujo.',
  'Tag removida': 'Etiqueta quitada',
  'Dispara quando a tag sai do contato.': 'Se dispara cuando la etiqueta sale del contacto.',
  'Campo alterado': 'Campo modificado',
  'Dispara quando um campo do perfil muda de valor.':
    'Se dispara cuando un campo del perfil cambia de valor.',
  'Etapa alterada': 'Etapa modificada',
  'Dispara quando a conversa muda de etapa no pipeline.':
    'Se dispara cuando la conversación cambia de etapa en el embudo.',
  'Responsável alterado': 'Responsable modificado',
  'Dispara quando a conversa troca de dono, ou fica sem.':
    'Se dispara cuando la conversación cambia de dueño, o queda sin uno.',
  'Clicou no link': 'Hizo clic en el enlace',
  'Dispara quando o contato abre um link rastreado de botão.':
    'Se dispara cuando el contacto abre un enlace rastreado de botón.',
  'Palavras (separadas por vírgula)': 'Palabras (separadas por coma)',
  'preço, valor, quanto custa': 'precio, valor, cuánto cuesta',
  'Olá {primeiro_nome}! Já já alguém te responde.':
    '¡Hola {primeiro_nome}! Al tiro te responde alguien.',
  'Botões (opcional)': 'Botones (opcional)',
  Resposta: 'Respuesta',
  Link: 'Enlace',
  'Quero saber mais': 'Quiero saber más',
  'Adicionar botão': 'Agregar botón',
  'No modo simples o clique entra na conversa como mensagem do cliente. Para cada botão levar a um caminho diferente é preciso o construtor de fluxo.':
    'En el modo simple el clic entra a la conversación como mensaje del cliente. Para que cada botón lleve a un camino distinto hace falta el constructor de flujo.',
  'Criar automação': 'Crear automatización',
  'A automação nasce em rascunho — publique na lista quando estiver pronta.':
    'La automatización nace como borrador — publícala desde la lista cuando esté lista.',
  Expediente: 'Horario de atención',
  ligado: 'activo',
  desligado: 'inactivo',
  Ligado: 'Activo',
  'Horário de Brasília, da equipe deste inbox. Um turno que termina antes de começar atravessa a meia-noite — 18:00 às 02:00 é o plantão da madrugada.':
    'Horario de Brasilia, del equipo de esta bandeja. Un turno que termina antes de empezar cruza la medianoche — 18:00 a 02:00 es el turno de madrugada.',
  Domingo: 'Domingo',
  Segunda: 'Lunes',
  Terça: 'Martes',
  Quarta: 'Miércoles',
  Quinta: 'Jueves',
  Sexta: 'Viernes',
  Sábado: 'Sábado',
  às: 'a las',
  'Salvar expediente': 'Guardar el horario',
  Descartar: 'Descartar',
  'Novo fluxo': 'Flujo nuevo',
  'A conta decide o que os blocos podem fazer — ela não muda depois.':
    'La cuenta decide lo que los bloques pueden hacer — y no se cambia después.',
  'Triagem do Comercial': 'Triaje de Comercial',
  'Criar e abrir o construtor': 'Crear y abrir el constructor',

  // ─── Construtor de fluxo — tela (routes/_app/automacoes.$id.tsx) ───────────────
  'Carregando o fluxo…': 'Cargando el flujo…',
  'Fluxo não encontrado.': 'Flujo no encontrado.',
  'alterações não salvas': 'cambios sin guardar',
  'Modo simples': 'Modo simple',
  Blocos: 'Bloques',
  'Comece pelo gatilho — é por onde o contato entra.':
    'Parte por el disparador — es por donde entra el contacto.',
  'Sair sem salvar?': '¿Salir sin guardar?',
  'As alterações deste fluxo ainda não foram gravadas.':
    'Los cambios de este flujo todavía no se han guardado.',
  'Você mexeu no fluxo e não salvou. Sair agora descarta o que foi montado desde a última gravação.':
    'Modificaste el flujo y no lo guardaste. Salir ahora descarta lo que armaste desde la última vez que guardaste.',
  'Salvar e ficar': 'Guardar y quedarme',
  'Descartar e sair': 'Descartar y salir',
  'Continuar editando': 'Seguir editando',

  // ─── Blocos do fluxo (components/fluxo/blocos.ts, NoDoFluxo.tsx) ───────────────
  Gatilho: 'Disparador',
  'Por onde o contato entra no fluxo': 'Por dónde entra el contacto al flujo',
  'Texto, anexo e botões': 'Texto, adjunto y botones',
  Espera: 'Espera',
  'Tempo, digitando, ou até o contato responder':
    'Tiempo, escribiendo, o hasta que el contacto responda',
  Condição: 'Condición',
  'Separa o caminho em sim e não': 'Separa el camino en sí y no',
  Ação: 'Acción',
  'Tag, campo, nota, etapa, resolver, parar bot':
    'Etiqueta, campo, nota, etapa, resolver, detener el bot',
  'sem gatilho': 'sin disparador',
  '{rotulo}: {valor}': '{rotulo}: {valor}',
  '{rotulo} · por campanha': '{rotulo} · por campaña',
  'sem texto': 'sin texto',
  '{texto} · {n} botão(ões)': '{texto} · {n} botón(es)',
  'até o contato responder': 'hasta que el contacto responda',
  'digitando por {tempo}': 'escribiendo por {tempo}',
  'está dentro do expediente?': '¿está dentro del horario de atención?',
  'disse: {palavras}': 'dijo: {palavras}',
  'tem a tag?': '¿tiene la etiqueta?',
  'veio da campanha?': '¿vino de la campaña?',
  condição: 'condición',
  ação: 'acción',
  'primeira mensagem': 'primer mensaje',
  'entrou pelo link': 'entró por el enlace',
  'palavra-chave': 'palabra clave',
  'fora do expediente': 'fuera del horario de atención',
  'contato criado': 'contacto creado',
  'tag adicionada': 'etiqueta agregada',
  'tag removida': 'etiqueta quitada',
  'campo alterado': 'campo modificado',
  'etapa alterada': 'etapa modificada',
  'responsável alterado': 'responsable modificado',
  'clicou no link': 'hizo clic en el enlace',
  'adiciona uma tag': 'agrega una etiqueta',
  'remove uma tag': 'quita una etiqueta',
  'escreve uma nota interna': 'escribe una nota interna',
  'move de etapa': 'mueve de etapa',
  'troca o responsável': 'cambia el responsable',
  'preenche um campo do perfil': 'llena un campo del perfil',
  'limpa um campo do perfil': 'limpia un campo del perfil',
  'marca a conversa como resolvida': 'marca la conversación como resuelta',
  'devolve a conversa para a fila': 'devuelve la conversación a la cola',
  'para o bot nesta conversa': 'detiene el bot en esta conversación',
  '{n} d': '{n} d',
  '{n} h': '{n} h',
  sim: 'sí',
  'botão {n}': 'botón {n}',
  'Duplicar este bloco': 'Duplicar este bloque',
  'Remover este bloco': 'Quitar este bloque',
  'botão sem texto': 'botón sin texto',
  'Copiar o link': 'Copiar el enlace',
  'não respondeu': 'no respondió',
  respondeu: 'respondió',
  'próxima etapa': 'siguiente paso',
  'nada leva a este bloco': 'nada lleva a este bloque',
  'Remover esta ligação': 'Quitar esta conexión',

  // ─── Painel do bloco (components/fluxo/PainelDoBloco.tsx) ──────────────────────
  'Fechar painel': 'Cerrar el panel',
  'Nome do bloco': 'Nombre del bloque',
  'Só para você se achar no construtor. Vazio mostra o tipo do bloco.':
    'Solo para que te ubiques en el constructor. Vacío muestra el tipo de bloque.',
  'Remover bloco': 'Quitar el bloque',
  'Como comparar': 'Cómo comparar',
  'A mensagem contém a palavra': 'El mensaje contiene la palabra',
  'A mensagem é exatamente a palavra': 'El mensaje es exactamente la palabra',
  'Qual tag': 'Qué etiqueta',
  'Qual etapa': 'Qué etapa',
  'Qual campanha': 'Qué campaña',
  'Qual campo': 'Qué campo',
  'Qualquer tag': 'Cualquier etiqueta',
  'Qualquer etapa': 'Cualquier etapa',
  Repetição: 'Repetición',
  'Cada contato entra uma vez só': 'Cada contacto entra una sola vez',
  'Desmarcado, quem voltar pelo mesmo caminho roda o fluxo de novo — e o que estava em andamento recomeça do início. Marque em boas-vindas, para não apresentar a empresa duas vezes para a mesma pessoa.':
    'Sin marcar, quien vuelva por el mismo camino corre el flujo de nuevo — y lo que estaba en curso vuelve a empezar. Márcalo en las bienvenidas, para no presentar la empresa dos veces a la misma persona.',
  'A entrada é': 'La entrada es',
  'Um identificador desta automação': 'Un identificador de esta automatización',
  'Uma campanha existente': 'Una campaña existente',
  'Esta conta ainda não tem campanha. Crie em Campanhas se quiser medir a origem por lá.':
    'Esta cuenta todavía no tiene campañas. Crea una en Campañas si quieres medir el origen desde ahí.',
  'Gerar outro identificador': 'Generar otro identificador',
  Gerar: 'Generar',
  'Vazio = qualquer campo': 'Vacío = cualquier campo',
  'Só para quem veio de': 'Solo para quien vino de',
  'Qualquer origem': 'Cualquier origen',
  'Este gatilho responde a quem entra por um link de campanha, e esta conta ainda não tem nenhuma. Crie em {campanhas} — é lá que mora o link, o texto de origem e o relatório de quem veio por ele.':
    'Este disparador responde a quien entra por un enlace de campaña, y esta cuenta todavía no tiene ninguna. Crea una en {campanhas} — ahí vive el enlace, el texto de origen y el reporte de quién vino por él.',
  'Olá {primeiro_nome}!': '¡Hola {primeiro_nome}!',
  'Como os botões aparecem': 'Cómo aparecen los botones',
  'Presos à mensagem': 'Pegados al mensaje',
  'No lugar do teclado do celular': 'En lugar del teclado del celular',
  'Este bloco tem botão de link, e o teclado do Telegram não abre endereços — por isso a segunda opção está indisponível. Troque o botão de link por um de resposta, ou remova-o, para poder usá-la.':
    'Este bloque tiene un botón de enlace, y el teclado de Telegram no abre direcciones — por eso la segunda opción está deshabilitada. Cambia el botón de enlace por uno de respuesta, o quítalo, para poder usarla.',
  'entra na conversa como mensagem dele': 'entra a la conversación como mensaje suyo',
  'Os botões ocupam o teclado do celular e somem depois do toque. O que o contato escolher {entra} — quem atende lê a escolha como uma frase. Não aceita botão de link.':
    'Los botones ocupan el teclado del celular y desaparecen después del toque. Lo que el contacto elija {entra} — quien atiende lee la elección como una frase. No acepta botón de enlace.',
  'Os botões ficam presos ao balão da mensagem e continuam lá depois do toque. É o único jeito de oferecer botão de link.':
    'Los botones quedan pegados a la burbuja del mensaje y siguen ahí después del toque. Es la única forma de ofrecer un botón de enlace.',
  'Resposta — vira um caminho no fluxo': 'Respuesta — se vuelve un camino en el flujo',
  'Link — abre uma URL': 'Enlace — abre una URL',
  'Rastrear cliques': 'Rastrear los clics',
  'Registra “Clicou no link” na conversa e libera o gatilho de mesmo nome. Em troca, o botão leva um endereço do CRM em vez do endereço final.':
    'Registra “Hizo clic en el enlace” en la conversación y habilita el disparador del mismo nombre. A cambio, el botón lleva una dirección del CRM en vez de la dirección final.',
  'Cada botão de resposta vira uma porta de saída no bloco. Ligue-a a outro bloco para o clique levar a um caminho próprio — sem ligação, o fluxo segue pela saída normal.':
    'Cada botón de respuesta se vuelve una salida del bloque. Conéctala a otro bloque para que el clic lleve a su propio camino — sin conexión, el flujo sigue por la salida normal.',
  'Conta business não aceita botão: o Telegram não permite teclado em mensagem enviada em nome dela. Para ramificar aqui, use um bloco de espera por resposta e uma condição de palavra-chave — é o “responda 1, 2 ou 3”.':
    'Una cuenta business no acepta botones: Telegram no permite teclado en un mensaje enviado en su nombre. Para ramificar acá, usa un bloque de espera por respuesta y una condición de palabra clave — es el “responde 1, 2 o 3”.',
  'Esperar o quê': 'Esperar qué',
  'Um tempo': 'Un tiempo',
  'Digitando por…': 'Escribiendo por…',
  'O contato responder': 'Que el contacto responda',
  'Digitando por': 'Escribiendo por',
  segundos: 'segundos',
  'Mostra “digitando…” no Telegram e só então continua — é o que faz a resposta parecer escrita na hora em vez de disparada. Máximo {n} segundos: o Telegram apaga o aviso sozinho por volta disso, e mais tempo deixaria o cliente olhando para nada. Para pausas longas, use “um tempo”.':
    'Muestra “escribiendo…” en Telegram y recién ahí sigue — es lo que hace que la respuesta parezca escrita al momento en vez de disparada. Máximo {n} segundos: Telegram borra el aviso solo por ahí, y más tiempo dejaría al cliente mirando nada. Para pausas largas, usa “un tiempo”.',
  'Quanto tempo': 'Cuánto tiempo',
  'Até um minuto a pausa é cumprida no segundo exato. Acima disso pode atrasar até um minuto — uma pausa de 90 segundos às vezes vira 2 minutos.':
    'Hasta un minuto la pausa se cumple en el segundo exacto. Sobre eso puede atrasarse hasta un minuto — una pausa de 90 segundos a veces se vuelve 2 minutos.',
  'Se não responder em': 'Si no responde en',
  'parar o bot': 'detener el bot',
  'Vencido o prazo, o fluxo segue pela saída {naoRespondeu} — dá para mandar a próxima mensagem da cadência, marcar uma tag, o que for. Quem responde a tempo sai pelo outro lado e não recebe a cutucada. Sem nada ligado em “não respondeu”, o fluxo simplesmente acaba ali; para calar o robô de propósito use a ação {pararBot}, que aparece no desenho.':
    'Vencido el plazo, el flujo sigue por la salida {naoRespondeu} — puedes mandar el siguiente mensaje de la cadencia, marcar una etiqueta, lo que sea. Quien responde a tiempo sale por el otro lado y no recibe el recordatorio. Sin nada conectado en “no respondió”, el flujo simplemente termina ahí; para callar al robot a propósito usa la acción {pararBot}, que aparece en el dibujo.',
  'Testar o quê': 'Probar qué',
  'Está dentro do expediente': 'Está dentro del horario de atención',
  'O que ele disse': 'Lo que dijo',
  'Tem a tag': 'Tiene la etiqueta',
  'Veio da campanha': 'Vino de la campaña',
  '1, sim, quero': '1, sí, quiero',
  'Contém a palavra': 'Contiene la palabra',
  'É exatamente a palavra': 'Es exactamente la palabra',
  'Fazer o quê': 'Hacer qué',
  Conversa: 'Conversación',
  'Adicionar uma tag': 'Agregar una etiqueta',
  'Remover uma tag': 'Quitar una etiqueta',
  'Preencher um campo do perfil': 'Llenar un campo del perfil',
  'Limpar um campo do perfil': 'Limpiar un campo del perfil',
  'Escrever nota interna': 'Escribir una nota interna',
  'Mover de etapa': 'Mover de etapa',
  'Trocar o responsável': 'Cambiar el responsable',
  'Marcar como resolvida': 'Marcar como resuelta',
  'Devolver para a fila': 'Devolver a la cola',
  'Parar o bot nesta conversa': 'Detener el bot en esta conversación',
  'Texto da nota': 'Texto de la nota',
  'Nota interna nunca vai para o Telegram — só aparece na conversa, para a equipe.':
    'La nota interna nunca va a Telegram — solo aparece en la conversación, para el equipo.',
  'Para qual etapa': 'A qué etapa',
  'Para quem': 'Para quién',
  'Ninguém — devolve para a fila': 'Nadie — la devuelve a la cola',
  cidade: 'ciudad',
  'A chave do campo personalizado, como ela aparece no perfil do contato.':
    'La clave del campo personalizado, tal como aparece en el perfil del contacto.',
  'Com qual valor': 'Con qué valor',
  '{primeiro_nome}': '{primeiro_nome}',
  'Move para a etapa final do inbox. A conversa sai da fila de abertas — e reabre sozinha se o cliente voltar a escrever.':
    'Mueve a la etapa final de la bandeja. La conversación sale de la cola de abiertas — y se reabre sola si el cliente vuelve a escribir.',
  'Volta para a etapa padrão e tira o responsável, como se fosse conversa nova na fila.':
    'Vuelve a la etapa por defecto y quita al responsable, como si fuera una conversación nueva en la cola.',
  'Trava a conversa contra qualquer automação até alguém reativar na tela do atendimento. Diferente de o fluxo simplesmente acabar: aquilo encerra só esta passagem e deixa a porta aberta para o próximo fluxo.':
    'Traba la conversación contra cualquier automatización hasta que alguien la reactive en la pantalla de atención. Distinto de que el flujo simplemente termine: eso cierra solo esta pasada y deja la puerta abierta al siguiente flujo.',
  Segundos: 'Segundos',
  Minutos: 'Minutos',
  Horas: 'Horas',
  Dias: 'Días',
  'Copiar link': 'Copiar el enlace',
  'Conta business não tem link de entrada com identificador: este link só pré-preenche a primeira mensagem, e o cliente pode editar antes de enviar. A atribuição é inferida, não exata.':
    'Una cuenta business no tiene enlace de entrada con identificador: este enlace solo precarga el primer mensaje, y el cliente puede editarlo antes de enviarlo. La atribución es inferida, no exacta.',
  'Sem dados ainda — a contagem começa no primeiro envio deste bloco.':
    'Todavía sin datos — la cuenta parte en el primer envío de este bloque.',
  'de CTR · {abriram} de {receberam} pessoa abriu':
    'de CTR · {abriram} de {receberam} persona abrió',
  'de CTR · {abriram} de {receberam} pessoas abriram':
    'de CTR · {abriram} de {receberam} personas abrieron',

  // ─── Anexo e contatos do fluxo (CampoDeAnexo.tsx, ContatosDaAutomacao.tsx) ─────
  Anexo: 'Adjunto',
  'arquivo anexado': 'archivo adjunto',
  imagem: 'imagen',
  vídeo: 'video',
  áudio: 'audio',
  arquivo: 'archivo',
  'O arquivo sobe uma vez e vai junto toda vez que este bloco enviar. Com anexo, o texto vira a legenda da mensagem.':
    'El archivo se sube una vez y va adjunto cada vez que este bloque envíe. Con adjunto, el texto pasa a ser la descripción del mensaje.',
  'Vídeo circular não aceita legenda no Telegram: o vídeo chega primeiro e o texto vem logo depois, em mensagem própria.':
    'El video circular no acepta descripción en Telegram: el video llega primero y el texto viene justo después, en un mensaje aparte.',
  'O vídeo circular precisa ser MP4 — o Telegram recusa outros formatos.':
    'El video circular tiene que ser MP4 — Telegram rechaza otros formatos.',
  'Contatos que já passaram por este fluxo': 'Contactos que ya pasaron por este flujo',
  'nome ou @username': 'nombre o @usuario',
  Buscar: 'Buscar',
  'Adicionar tag em todos': 'Agregar etiqueta a todos',
  'veio da triagem': 'vino del triaje',
  'A tag é adicionada a todos os contatos do fluxo, não apenas aos que a busca está mostrando. Se ela ainda não existir, é criada.':
    'La etiqueta se agrega a todos los contactos del flujo, no solo a los que muestra la búsqueda. Si todavía no existe, se crea.',
  'Ninguém com esse nome passou por aqui.': 'Nadie con ese nombre pasó por acá.',
  'Ninguém passou por este fluxo ainda.': 'Todavía no ha pasado nadie por este flujo.',

  // ─── Componentes de apoio ──────────────────────────────────────────────────────
  'Buscar emoji…': 'Buscar emoji…',
  Frequentes: 'Frecuentes',
  Rostos: 'Caras',
  Atendimento: 'Atención',
  Dinheiro: 'Dinero',
  Sinais: 'Señales',
  Resultados: 'Resultados',
  Negrito: 'Negrita',
  Itálico: 'Cursiva',
  Riscado: 'Tachado',
  Código: 'Código',
  'Link (⌘K)': 'Enlace (⌘K)',
  'Abrir o seletor de cores do sistema': 'Abrir el selector de colores del sistema',
  'Escolher no seletor de cores do sistema': 'Elegir en el selector de colores del sistema',
  'Cor em hexadecimal': 'Color en hexadecimal',
  Usar: 'Usar',
  'Inserir:': 'Insertar:',
  'O navegador bloqueou o microfone.': 'El navegador bloqueó el micrófono.',
  'Não foi possível gravar áudio aqui.': 'No se pudo grabar audio acá.',
  'Este navegador não grava áudio.': 'Este navegador no graba audio.',
  'ID do Telegram': 'ID de Telegram',
  'Primeiro nome': 'Nombre',
  Sobrenome: 'Apellido',
  'Usuário (@)': 'Usuario (@)',
  'Data de inscrição': 'Fecha de registro',
  'Status (ativo/bloqueado)': 'Estado (activo/bloqueado)',

  // ─── Mensagens que nascem no servidor (apps/server) e aparecem na tela ─────────
  'A transmissão precisa de texto ou de pelo menos um anexo.':
    'La difusión necesita texto o al menos un adjunto.',
  'Agendamento não encontrado': 'Programación no encontrada',
  'Anexo não encontrado': 'Adjunto no encontrado',
  'Apenas o dono do workspace pode conceder ou remover o papel de dono.':
    'Solo el dueño del workspace puede dar o quitar el rol de dueño.',
  'Apenas o dono pode remover outro dono.': 'Solo el dueño puede quitar a otro dueño.',
  'Automação não encontrada': 'Automatización no encontrada',
  'Bot não encontrado': 'Bot no encontrado',
  'Cadastro só por convite. Peça um link a quem administra o workspace.':
    'El registro es solo por invitación. Pídele un enlace a quien administra el workspace.',
  'Campanha não encontrada': 'Campaña no encontrada',
  'Canal não encontrado': 'Canal no encontrado',
  'Conta não encontrada': 'Cuenta no encontrada',
  'Contato não encontrado': 'Contacto no encontrado',
  'Conversa não encontrada': 'Conversación no encontrada',
  'Convite expirado': 'Invitación expirada',
  'Convite já foi usado': 'La invitación ya fue usada',
  'Convite não encontrado': 'Invitación no encontrada',
  'Convite revogado': 'Invitación revocada',
  'Escolha um horário pelo menos um minuto à frente':
    'Elige un horario al menos un minuto más adelante',
  'Escolha um horário pelo menos um minuto à frente.':
    'Elige un horario al menos un minuto más adelante.',
  'Escolha uma tag': 'Elige una etiqueta',
  'Esta conexão não concede o direito de responder':
    'Esta conexión no concede el permiso de responder',
  'Esta importação já começou.': 'Esta importación ya empezó.',
  'Esta mensagem já foi enviada': 'Este mensaje ya fue enviado',
  'Esta mensagem não está no Telegram.': 'Este mensaje no está en Telegram.',
  'Esta pessoa não está no workspace': 'Esta persona no está en el workspace',
  'Esta transmissão já terminou.': 'Esta difusión ya terminó.',
  'Este bot já tem um canal. Mova-o de inbox em vez de criar outro.':
    'Este bot ya tiene un canal. Muévelo de bandeja en vez de crear otro.',
  'Este convite não vale mais. Peça um link novo.':
    'Esta invitación ya no sirve. Pide un enlace nuevo.',
  'Este inbox não tem etapa de fechamento configurada.':
    'Esta bandeja no tiene etapa de cierre configurada.',
  'Etapa não pertence a este inbox': 'La etapa no pertenece a esta bandeja',
  'Horário inválido.': 'Horario inválido.',
  'Importação não encontrada': 'Importación no encontrada',
  'Inbox inválido para este workspace': 'Bandeja inválida para este workspace',
  'Inbox não encontrado': 'Bandeja no encontrada',
  'Lead não encontrado': 'Contacto no encontrado',
  'Marque pelo menos um dia de atendimento antes de ligar o expediente.':
    'Marca al menos un día de atención antes de activar el horario.',
  'Membro não encontrado': 'Miembro no encontrado',
  'Mensagem não encontrada': 'Mensaje no encontrado',
  'Mova ou desconecte as contas deste inbox antes de arquivá-lo.':
    'Mueve o desconecta las cuentas de esta bandeja antes de archivarla.',
  'Mova ou remova as contas deste inbox antes de apagá-lo.':
    'Mueve o quita las cuentas de esta bandeja antes de eliminarla.',
  'Nenhuma conversa neste recorte — ajuste os filtros.':
    'Ninguna conversación en este filtro — ajústalo.',
  'O fluxo precisa de um gatilho antes de entrar no ar.':
    'El flujo necesita un disparador antes de salir al aire.',
  'O gatilho de link precisa de um identificador ou de uma campanha.':
    'El disparador de enlace necesita un identificador o una campaña.',
  'O gatilho não leva a nenhum bloco — o fluxo não faria nada.':
    'El disparador no lleva a ningún bloque — el flujo no haría nada.',
  'O nome digitado não confere com o da conta.':
    'El nombre que escribiste no coincide con el de la cuenta.',
  'O nome digitado não confere com o do inbox.':
    'El nombre que escribiste no coincide con el de la bandeja.',
  'O Telegram não permite que o bot reaja por uma conta business.':
    'Telegram no permite que el bot reaccione por una cuenta business.',
  'Requer permissão de administrador': 'Requiere permiso de administrador',
  'Resposta não encontrada': 'Respuesta no encontrada',
  'Sem acesso a este inbox': 'Sin acceso a esta bandeja',
  'Sem acesso a este lead': 'Sin acceso a este contacto',
  'Sem acesso a este workspace': 'Sin acceso a este workspace',
  'Sessão inválida': 'Sesión inválida',
  'Só administradores criam resposta rápida para todos os inboxes.':
    'Solo los administradores crean respuestas rápidas para todas las bandejas.',
  'Só administradores mexem nas respostas rápidas de todos os inboxes.':
    'Solo los administradores modifican las respuestas rápidas de todas las bandejas.',
  'Tag não encontrada': 'Etiqueta no encontrada',
  'Transmissão não encontrada': 'Difusión no encontrada',
  'Um fluxo tem um gatilho só — é por ele que todo mundo entra.':
    'Un flujo tiene un solo disparador — es por ahí que entra todo el mundo.',
  'Uma ligação aponta para um bloco que não existe no fluxo.':
    'Una conexión apunta a un bloque que no existe en el flujo.',
  'Você não pode alterar o próprio papel. Peça a outro administrador.':
    'No puedes cambiar tu propio rol. Pídeselo a otro administrador.',
  'Você não pode remover a si mesmo do workspace.': 'No puedes quitarte a ti mismo del workspace.',
  'Workspace não informado': 'Workspace no informado',
  'Erro interno': 'Error interno',

  // Álbum — mídias que saem juntas, numa grade só.
  Álbum: 'Álbum',
  'Álbum sem mídias': 'Álbum sin archivos',
  'álbum de {n} mídias': 'álbum de {n} archivos',
  'As mídias chegam juntas, numa grade, e o texto do bloco vira a legenda da primeira. Álbum não aceita botão.':
    'Los archivos llegan juntos, en una grilla, y el texto del bloque pasa a ser la descripción del primero. El álbum no acepta botones.',
  'Com duas mídias ou mais a mensagem vira um álbum, e o Telegram não aceita botão em álbum. Para ramificar depois de um álbum, ligue este bloco a um de mensagem com botões.':
    'Con dos archivos o más el mensaje pasa a ser un álbum, y Telegram no acepta botones en un álbum. Para ramificar después de un álbum, conecta este bloque a uno de mensaje con botones.',
  'Este bloco tem botão, e o Telegram não aceita botão em álbum. Remova os botões para anexar mais de uma mídia.':
    'Este bloque tiene botones, y Telegram no acepta botones en un álbum. Quita los botones para adjuntar más de un archivo.',

  // Partes do bloco de mensagem — a sequência com pausa entre uma e outra.
  'Adicionar mensagem': 'Agregar mensaje',
  'Adicionar pausa': 'Agregar pausa',
  'Mensagem {n}': 'Mensaje {n}',
  'Remover mensagem': 'Quitar el mensaje',
  Pausa: 'Pausa',
  'Remover pausa': 'Quitar la pausa',
  'Mover para cima': 'Subir',
  'Mover para baixo': 'Bajar',
  'Mostrar “digitando…” durante a pausa': 'Mostrar “escribiendo…” durante la pausa',
  'digitando {n}s': 'escribiendo {n}s',
  'pausa de {n}s': 'pausa de {n}s',
  'A pausa vai até {n} segundos. Para esperar mais, use um bloco de espera.':
    'La pausa llega hasta {n} segundos. Para esperar más, usa un bloque de espera.',
  'Um bloco leva no máximo {n} mensagens. Para mais, ligue a outro bloco.':
    'Un bloque admite {n} mensajes como máximo. Para más, conéctalo a otro bloque.',
  'As mensagens deste bloco saem na ordem, uma depois da outra. A pausa entre duas delas faz a conversa parecer escrita na hora em vez de despejada de uma vez.':
    'Los mensajes de este bloque salen en orden, uno después del otro. La pausa entre dos de ellos hace que la conversación parezca escrita en el momento y no soltada de una vez.',
  'Só a última mensagem do bloco pode usar o teclado do celular: ele é um só para a conversa, e a mensagem seguinte o substituiria.':
    'Solo el último mensaje del bloque puede usar el teclado del teléfono: es uno solo para toda la conversación, y el mensaje siguiente lo reemplazaría.',
  'Esta mensagem tem botão, e o Telegram não aceita botão em álbum. Remova os botões para anexar mais de uma mídia.':
    'Este mensaje tiene botones, y Telegram no acepta botones en un álbum. Quita los botones para adjuntar más de un archivo.',
  'Com duas mídias ou mais a mensagem vira um álbum, e o Telegram não aceita botão em álbum. Para ramificar depois de um álbum, ponha os botões numa mensagem seguinte.':
    'Con dos archivos o más el mensaje pasa a ser un álbum, y Telegram no acepta botones en un álbum. Para ramificar después de un álbum, pon los botones en un mensaje siguiente.',

  // A condição testa, não espera — a confusão mais comum do construtor.
  'compara com a mensagem que iniciou o fluxo — falta uma espera antes':
    'compara con el mensaje que inició el flujo — falta una espera antes',
  'o toque no botão já é a resposta — esta espera aguarda outra mensagem e o fluxo para aqui':
    'tocar el botón ya es la respuesta — esta espera aguarda otro mensaje y el flujo se detiene aquí',
  'A condição compara na hora, sem esperar nada. Para decidir sobre uma resposta nova, ponha antes dela um bloco de espera “até o contato responder” — é ele que tem o prazo e a saída de quem não respondeu.':
    'La condición compara al instante, sin esperar nada. Para decidir sobre una respuesta nueva, pon antes un bloque de espera “hasta que el contacto responda” — es ese el que tiene el plazo y la salida de quien no respondió.',
  'Use “exatamente” em menu numérico: com “contém”, quem escreve “10 reais” cairia na opção 1.':
    'Usa “exactamente” en un menú numérico: con “contiene”, quien escribe “10 pesos” caería en la opción 1.',

  // A condição que espera a resposta antes de comparar.
  'Quando comparar': 'Cuándo comparar',
  'Esperar o contato responder': 'Esperar a que el contacto responda',
  'espera e compara: {palavras}': 'espera y compara: {palavras}',
  'O fluxo para aqui e compara a resposta que chegar. Quem responde algo da lista sai por “sim”, qualquer outra coisa sai por “não”.':
    'El flujo se detiene aquí y compara la respuesta que llegue. Quien responde algo de la lista sale por “sí”, cualquier otra cosa sale por “no”.',
  'Desligado, a condição compara na hora com o que o contato disse por último — logo depois de uma mensagem, isso é a frase que iniciou o fluxo, e o fluxo passa direto.':
    'Apagado, la condición compara al instante con lo último que dijo el contacto — justo después de un mensaje, eso es la frase que inició el flujo, y el flujo pasa de largo.',
  'Vencido o prazo, o fluxo sai pela terceira porta, {naoRespondeu} — é onde vai a cutucada, a tag de frio ou o parar o bot. Sem ela ligada em nada, a execução termina ali, e quem não respondeu não fica preso para sempre.':
    'Vencido el plazo, el flujo sale por la tercera puerta, {naoRespondeu} — ahí va el recordatorio, la etiqueta de frío o el detener el bot. Sin nada conectado ahí, la ejecución termina en ese punto, y quien no respondió no queda atrapado para siempre.',
  'Ficou um tempo na etapa': 'Se quedó un tiempo en la etapa',
  Tempo: 'Tiempo',
  'Parado nela por': 'Detenido en ella por',
  'O fluxo começa sozinho para quem completar esse tempo na etapa sem sair dela, com até um minuto de atraso. Quem sair e voltar conta de novo. Quem já estava parado antes de o fluxo ser ligado não entra.':
    'El flujo empieza solo para quien complete ese tiempo en la etapa sin salir de ella, con hasta un minuto de retraso. Quien sale y vuelve cuenta de nuevo. Quien ya estaba detenido antes de activar el flujo no entra.',
  'Passa quando': 'Pasa cuando',
  'Todas as regras são verdadeiras': 'Todas las reglas se cumplen',
  'Qualquer uma das regras é verdadeira': 'Cualquiera de las reglas se cumple',
  'Regra {n}': 'Regla {n}',
  'Remover regra': 'Quitar la regla',
  'Adicionar regra': 'Agregar regla',
  'Uma das regras espera o contato responder, então o bloco inteiro espera: todas as regras são conferidas quando a resposta chegar.':
    'Una de las reglas espera que el contacto responda, así que el bloque entero espera: todas las reglas se revisan cuando llegue la respuesta.',
  'Está na etapa': 'Está en la etapa',
  'Outra regra deste bloco já espera a resposta, e esta compara com a mesma resposta.':
    'Otra regla de este bloque ya espera la respuesta, y esta compara con la misma respuesta.',
  'Há quanto tempo': 'Hace cuánto tiempo',
  'Só se estiver nela há pelo menos': 'Solo si lleva en ella al menos',
  'Conta desde que a conversa entrou nessa etapa. Para “se continuar em Aguardando depois de um dia”, ponha um bloco de espera antes desta condição.':
    'Cuenta desde que la conversación entró en esa etapa. Para “si sigue en Esperando después de un día”, pon un bloque de espera antes de esta condición.',
  'Dispara quando a conversa completa o tempo escolhido parada na mesma etapa.':
    'Se activa cuando la conversación completa el tiempo elegido detenida en la misma etapa.',
  ' e ': ' y ',
  ' ou ': ' o ',
  'na etapa há {tempo}?': '¿en la etapa hace {tempo}?',
  'está na etapa?': '¿está en la etapa?',
  'parado na etapa': 'detenido en la etapa',
  'Escolha a etapa do gatilho': 'Elige la etapa del disparador',
  'Diga quanto tempo parado na etapa dispara o fluxo':
    'Indica cuánto tiempo detenido en la etapa activa el flujo',
  'Só uma regra de palavra-chave por bloco pode esperar a resposta':
    'Solo una regla de palabra clave por bloque puede esperar la respuesta',
  'Um bloco deste fluxo tem o mesmo identificador de um bloco de outra automação.':
    'Un bloque de este flujo tiene el mismo identificador que un bloque de otra automatización.',
  'tag apagada': 'etiqueta eliminada',
  'etapa apagada': 'etapa eliminada',
  'campanha apagada': 'campaña eliminada',
  'parado em {etapa} por {tempo}': 'detenido en {etapa} por {tempo}',
  'cada contato entra uma vez só': 'cada contacto entra una sola vez',
  'até o contato responder · prazo {tempo}': 'hasta que el contacto responda · plazo {tempo}',
  'espera {tempo}': 'espera {tempo}',
  'passa se todas:': 'pasa si se cumplen todas:',
  'passa se qualquer uma:': 'pasa si se cumple cualquiera:',
  'dentro do expediente': 'dentro del horario de atención',
  'disse: {palavras} · espera {tempo}': 'dijo: {palavras} · espera {tempo}',
  'tem a tag {tag}': 'tiene la etiqueta {tag}',
  'veio da campanha {campanha}': 'vino de la campaña {campanha}',
  'está em {etapa} há {tempo}': 'está en {etapa} hace {tempo}',
  'está em {etapa}': 'está en {etapa}',
  '+ tag {tag}': '+ etiqueta {tag}',
  '− tag {tag}': '− etiqueta {tag}',
  'nota: {texto}': 'nota: {texto}',
  'mover para {etapa}': 'mover a {etapa}',
  'responsável: {pessoa}': 'responsable: {pessoa}',
  'pessoa removida': 'persona eliminada',
  'sem responsável': 'sin responsable',
  'campo {campo} = {valor}': 'campo {campo} = {valor}',
  'limpar campo {campo}': 'limpiar campo {campo}',
  'marca como resolvida': 'marca como resuelta',
  'devolve para a fila': 'devuelve a la fila',
  'Ação {n}': 'Acción {n}',
  'Remover ação': 'Quitar la acción',
  'Adicionar ação': 'Agregar acción',
  'As ações rodam na ordem, uma depois da outra. Se uma falhar — uma tag que foi apagada, por exemplo —, as outras rodam mesmo assim.':
    'Las acciones se ejecutan en orden, una después de la otra. Si una falla —una etiqueta que fue eliminada, por ejemplo—, las demás se ejecutan igual.',
  'Parar o bot precisa ser a última ação do bloco':
    'Detener el bot tiene que ser la última acción del bloque',

  // ─── Meta Ads: Pixel, Conversions API e página-ponte (routes/_app/meta.tsx) ─
  'Meta Ads': 'Meta Ads',
  'Manda para a Meta quem chegou pela live e quem depositou, para as campanhas aprenderem com quem converte de verdade.':
    'Envía a Meta quién llegó por el live y quién depositó, para que las campañas aprendan de quien convierte de verdad.',
  'Pixel e Conversions API': 'Píxel y API de conversiones',
  'O Pixel roda na página-ponte; o token deixa o servidor mandar o depósito, que acontece longe do navegador.':
    'El píxel corre en la página puente; el token deja que el servidor envíe el depósito, que ocurre lejos del navegador.',
  'ID do Pixel': 'ID del píxel',
  'Token da Conversions API': 'Token de la API de conversiones',
  'Gravado, termina em {final}. Deixe vazio para manter.':
    'Guardado, termina en {final}. Déjalo vacío para mantenerlo.',
  'Gerado no Gerenciador de Eventos, em Configurações do Pixel.':
    'Se genera en el Administrador de eventos, en la configuración del píxel.',
  'Campo do telefone': 'Campo del teléfono',
  'Vai com hash, e é o que mais ajuda a Meta a reconhecer a pessoa.':
    'Va con hash, y es lo que más ayuda a Meta a reconocer a la persona.',
  'Campo do e-mail': 'Campo del correo',
  'Opcional.': 'Opcional.',
  'Código de teste': 'Código de prueba',
  'Só enquanto confere na aba “Testar eventos”. Com ele preenchido, nada conta para as campanhas.':
    'Solo mientras revisas en la pestaña “Probar eventos”. Con él lleno, nada cuenta para las campañas.',
  'O código de teste está ligado: os eventos vão só para a aba de teste e não contam para as campanhas.':
    'El código de prueba está activo: los eventos van solo a la pestaña de prueba y no cuentan para las campañas.',
  'Enviar eventos para a Meta': 'Enviar eventos a Meta',
  'Mandar “Lead” quando o contato chega ao bot pela página-ponte':
    'Enviar “Lead” cuando el contacto llega al bot por la página puente',
  'A Meta recebeu o evento de teste. Confira na aba “Testar eventos”.':
    'Meta recibió el evento de prueba. Revísalo en la pestaña “Probar eventos”.',
  'A Meta recusou: {erro}': 'Meta lo rechazó: {erro}',
  'Testando…': 'Probando…',
  'Testar conexão': 'Probar conexión',
  'Tag que vira conversão': 'Etiqueta que se vuelve conversión',
  'Quando a tag é marcada num contato, o evento sai para a Meta — uma vez por pessoa, mesmo que a tag seja tirada e marcada de novo.':
    'Cuando la etiqueta se marca en un contacto, el evento sale a Meta — una vez por persona, aunque la etiqueta se quite y se vuelva a marcar.',
  'Nova regra': 'Nueva regla',
  'Nenhuma regra ainda. Crie uma para a tag de primeiro depósito.':
    'Todavía no hay reglas. Crea una para la etiqueta de primer depósito.',
  'valor do campo {campo}, em {moeda}': 'valor del campo {campo}, en {moeda}',
  'sem valor': 'sin valor',
  'Editar regra': 'Editar regla',
  Evento: 'Evento',
  'Campo do valor': 'Campo del valor',
  'Preenchido, a tela pede o valor antes de deixar marcar a tag. Vazio, o evento vai sem valor.':
    'Si está lleno, la pantalla pide el valor antes de dejar marcar la etiqueta. Vacío, el evento va sin valor.',
  Moeda: 'Moneda',
  'Compra (Purchase)': 'Compra (Purchase)',
  'Cadastro concluído (CompleteRegistration)': 'Registro completado (CompleteRegistration)',
  'Contato (Contact)': 'Contacto (Contact)',
  'Assinatura (Subscribe)': 'Suscripción (Subscribe)',
  'Página-ponte das lives': 'Página puente de los lives',
  'Vai no botão da live no lugar do t.me. Dispara o Pixel e segue para o bot em menos de dois segundos, levando junto quem clicou.':
    'Va en el botón del live en lugar del t.me. Dispara el píxel y sigue al bot en menos de dos segundos, llevando consigo a quien hizo clic.',
  'Novo link': 'Nuevo enlace',
  Cliques: 'Clics',
  'Chegaram ao bot': 'Llegaron al bot',
  Conversões: 'Conversiones',
  'Valor convertido': 'Valor convertido',
  'Nenhum link ainda. Crie um por origem: cada live, a bio, os stories.':
    'Todavía no hay enlaces. Crea uno por origen: cada live, la bio, las historias.',
  '{conta} · chave {chave}': '{conta} · clave {chave}',
  '{cliques} cliques · {leads} no bot · {conversoes} conversões · {valor} em valor':
    '{cliques} clics · {leads} en el bot · {conversoes} conversiones · {valor} en valor',
  'Editar link': 'Editar enlace',
  'A chave é a mesma do /start que a automação da live já espera — o fluxo continua igual.':
    'La clave es la misma del /start que la automatización del live ya espera — el flujo sigue igual.',
  'Chave do /start': 'Clave del /start',
  'Letras, números, _ e - (até 53)': 'Letras, números, _ y - (hasta 53)',
  'Link ligado': 'Enlace activo',
  'Eventos recentes': 'Eventos recientes',
  'Os últimos cinquenta mandados à Meta, com o motivo de quem falhou.':
    'Los últimos cincuenta enviados a Meta, con el motivo de los que fallaron.',
  'Nenhum evento ainda.': 'Todavía no hay eventos.',
  'na fila': 'en cola',
  enviando: 'enviando',
  enviado: 'enviado',
  'Chegou pela página-ponte {origem} · {quando}': 'Llegó por la página puente {origem} · {quando}',
  'Valor do depósito': 'Valor del depósito',
  'A tag “{tag}” manda a conversão para a Meta, e ela precisa do valor.':
    'La etiqueta “{tag}” envía la conversión a Meta, y necesita el valor.',
  'Valor em {moeda}': 'Valor en {moeda}',
  'Marcar tag': 'Marcar etiqueta',
  'Preencha o valor do depósito antes de marcar esta tag':
    'Completa el valor del depósito antes de marcar esta etiqueta',
  'Cole o token da Conversions API': 'Pega el token de la API de conversiones',
  'Preencha o código de teste para testar a conexão':
    'Completa el código de prueba para probar la conexión',
  'Esta tag já tem uma regra': 'Esta etiqueta ya tiene una regla',
  'Esta tag não pede valor': 'Esta etiqueta no pide valor',
  'Valor inválido': 'Valor inválido',
  'A página-ponte só funciona com bot': 'La página puente solo funciona con bot',
  'Este endereço já está em uso': 'Esta dirección ya está en uso',
  'Moeda em três letras, como CLP': 'Moneda en tres letras, como CLP',
  'A chave usa letras, números, _ e -': 'La clave usa letras, números, _ y -',
  // Motivos de falha gravados pelo servidor (services/meta/capi.ts). Os que a
  // própria Meta devolve chegam em inglês e passam sem tradução.
  'integração com a Meta desligada': 'integración con Meta desactivada',
  'falta o token da Conversions API': 'falta el token de la API de conversiones',
  'contato apagado': 'contacto eliminado',
  'não foi possível enviar em 72 horas': 'no se pudo enviar en 72 horas',

  // ─── Campos do perfil, botão de pedir telefone e espera que guarda resposta ─
  'Clique para editar': 'Haz clic para editar',
  'Editar {campo}': 'Editar {campo}',
  'Apagar o campo {campo}': 'Eliminar el campo {campo}',
  campo: 'campo',
  'Nome do campo': 'Nombre del campo',
  valor: 'valor',
  'Valor do campo': 'Valor del campo',
  '+ campo': '+ campo',
  'Editar telefone': 'Editar teléfono',
  'Pedir telefone — o contato manda o número': 'Pedir teléfono — el contacto envía su número',
  'Ao tocar, o Telegram pergunta se a pessoa quer compartilhar o número. O número verificado vira o telefone do contato, e o fluxo segue pela porta deste botão.':
    'Al tocarlo, Telegram pregunta si la persona quiere compartir su número. El número verificado pasa a ser el teléfono del contacto, y el flujo sigue por la salida de este botón.',
  'Para pedir o telefone, ponha os botões no lugar do teclado do celular.':
    'Para pedir el teléfono, pon los botones en lugar del teclado del celular.',
  'Guardar a resposta no campo': 'Guardar la respuesta en el campo',
  'rut, telefone, nome…': 'rut, teléfono, nombre…',
  'Opcional. O que o contato responder fica guardado neste campo do perfil, e o bloco seguinte já o encontra preenchido. Se ele tocar no botão de pedir telefone, o número entra aqui.':
    'Opcional. Lo que el contacto responda queda guardado en este campo del perfil, y el bloque siguiente ya lo encuentra lleno. Si toca el botón de pedir teléfono, el número queda aquí.',
  'guarda em {campo}': 'guarda en {campo}',
  'Pedir telefone': 'Pedir teléfono',
  'O botão de pedir telefone só funciona no teclado do Telegram':
    'El botón de pedir teléfono solo funciona en el teclado de Telegram',
}
